import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../services/api';
import NotificationBell from '../components/NotificationBell';

import ProfileEditor from '../components/ProfileEditor';
import CandidateProfileView from '../components/CandidateProfileView';
import StaffJobsTab from '../components/StaffJobsTab';
import { calculateProfileCompletion } from '../utils/profileCompletion';
import {
  Users,
  UserPlus,
  Search,
  X,
  CheckCircle,
  XCircle,
  Trash2,
  Mail,
  Briefcase,
  GraduationCap,
  Award,
  Languages,
  Settings,
  LogOut,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  UserCheck,
  UserX,
  FileText,
  Bell,
  Check,
  ShoppingCart,
  Download,
  Key,
  MessageSquare,
  Send,
  AlertCircle,
  Calendar,
  Heart,
  Database,
  Lock,
  ArrowLeft,
  ArrowRight,
  User,
  Plus,
  BadgeCheck,
  RefreshCw,
  Globe,
  Phone,
  Menu,
  Video
} from 'lucide-react';
import ScheduleInterviewModal from '../components/ScheduleInterviewModal';

const SidebarItem = ({ icon: Icon, label, active, onClick, badge, hasSubmenu, isOpen }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all duration-200 group relative overflow-hidden ${active
      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/20'
      : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
      }`}
  >
    <div className="flex items-center gap-3 relative z-10 w-full">
      <div className={`p-1.5 rounded-lg transition-colors ${active ? 'bg-white/20' : 'bg-slate-800 group-hover:bg-slate-700'
        }`}>
        <Icon className={`h-4 w-4 ${active ? 'text-white' : 'text-slate-400 group-hover:text-blue-400'}`} />
      </div>
      <span className="font-medium text-sm truncate flex-1 text-left">{label}</span>
      {badge && (
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${active ? 'bg-white/20 text-white' : 'bg-blue-500/10 text-blue-400'
          }`}>
          {badge}
        </span>
      )}
      {hasSubmenu && (
        <ChevronRight className={`h-3.5 w-3.5 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''
          } ${active ? 'text-white/70' : 'text-slate-500'}`} />
      )}
    </div>
    {active && (
      <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
    )}
  </button>
);

const StaffDashboard = () => {
  const { user: currentUser, logout } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine active tab from URL path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/approvals')) return 'approvals';
    if (path.includes('/recruiter-visibility')) return 'recruiter-visibility';
    if (path.includes('/recruiter-requests')) return 'recruiter-requests';
    if (path.includes('/recruiter-interests')) return 'recruiter-interests';
    if (path.includes('/recruiter-feedbacks')) return 'recruiter-feedbacks';
    if (path.includes('/recruiting-jobs')) return 'recruiting-jobs';
    if (path.includes('/interviews')) return 'interviews';
    if (path.includes('/sourcing-partners')) return 'sourcing-partners';
    if (path.includes('/sourcing-feedbacks')) return 'sourcing-feedbacks';
    if (path.includes('/notifications')) return 'notifications';
    if (path.includes('/profile/')) return 'profile';
    return 'overview';
  };
  const activeTab = getActiveTab();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [languageSort, setLanguageSort] = useState('none');
  const [approvalStatus, setApprovalStatus] = useState('pending');
  const [approvalsMenuOpen, setApprovalsMenuOpen] = useState(true);
  
  // Pagination state for approvals
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalApprovals, setTotalApprovals] = useState(0);

  // Load active approval tab from localStorage, default to 'pending'
  const [activeApprovalTab, setActiveApprovalTab] = useState(() => {
    const savedApprovalTab = localStorage.getItem('staffDashboard_activeApprovalTab');
    return savedApprovalTab || 'pending';
  });

  // Redirect to overview if on base staff path
  useEffect(() => {
    if (location.pathname === '/staff' || location.pathname === '/staff/') {
      navigate('/staff/overview', { replace: true });
    }
  }, [location.pathname, navigate]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editUserData, setEditUserData] = useState({});

  // Feedback modal is used for viewing Partner Feedbacks & Requirements (not for sending to candidates)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordResetUserId, setPasswordResetUserId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'candidate',
    approval_status: 'approved',
    profile_status: 'incomplete'
  });
  const [interviews, setInterviews] = useState([]);
  const [showScheduleInterviewModal, setShowScheduleInterviewModal] = useState(false);
  const [interviewCandidate, setInterviewCandidate] = useState(null);
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    candidates: 0,
    recruiters: 0
  });
  const [recruiters, setRecruiters] = useState([]);
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [recruiterRequests, setRecruiterRequests] = useState([]);
  const [visibleCandidates, setVisibleCandidates] = useState([]);
  const [allCandidatesForVisibility, setAllCandidatesForVisibility] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [sourcingCandidates, setSourcingCandidates] = useState([]);
  const [sourcingStatusFilter, setSourcingStatusFilter] = useState('all');
  const [sourcingPartners, setSourcingPartners] = useState([]);
  const [selectedSourcingPartner, setSelectedSourcingPartner] = useState(null);
  const [showPartnerCandidates, setShowPartnerCandidates] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRequestCandidates, setShowRequestCandidates] = useState(false);
  const [candidateStatistics, setCandidateStatistics] = useState([]);
  const [manualPools, setManualPools] = useState([]);
  const [loadingPools, setLoadingPools] = useState(false);
  const [showCreatePoolModal, setShowCreatePoolModal] = useState(false);
  const [newPoolName, setNewPoolName] = useState('');
  const [newPoolIsPublic, setNewPoolIsPublic] = useState(false);
  const [selectedPool, setSelectedPool] = useState(null);
  const [showPoolCandidatesModal, setShowPoolCandidatesModal] = useState(false);
  const [poolCandidates, setPoolCandidates] = useState([]);
  const [showAddToPoolModal, setShowAddToPoolModal] = useState(false);
  const [candidateToAddToPool, setCandidateToAddToPool] = useState(null);

  // Reject Request Modal state
  const [showRejectRequestModal, setShowRejectRequestModal] = useState(false);
  const [requestToReject, setRequestToReject] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingRequest, setRejectingRequest] = useState(false);

  // New state for adding candidate TO a pool (reverse flow)
  const [showSelectCandidateModal, setShowSelectCandidateModal] = useState(false);
  const [candidateSearchQuery, setCandidateSearchQuery] = useState('');
  const [partnerFeedbacks, setPartnerFeedbacks] = useState([]);
  const [feedbackStatusFilter, setFeedbackStatusFilter] = useState('all');
  const [feedbackPartnerTypeFilter, setFeedbackPartnerTypeFilter] = useState('all');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [feedbackResponses, setFeedbackResponses] = useState([]);

  const [responseText, setResponseText] = useState('');
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [notificationDateSort, setNotificationDateSort] = useState('desc'); // 'asc' | 'desc'
  const [notificationStatusFilter, setNotificationStatusFilter] = useState('all'); // all | unread | read  

  // Recruiter interests state
  const [recruiterInterests, setRecruiterInterests] = useState([]);
  const [interestStatusFilter, setInterestStatusFilter] = useState('pending');
  const [interestTypeFilter, setInterestTypeFilter] = useState('all');

  // Candidate selection state (for cart functionality)
  const [candidateSearchTerm, setCandidateSearchTerm] = useState('');
  const [selectedOccupationFilter, setSelectedOccupationFilter] = useState('all');
  const [selectedCandidatesCart, setSelectedCandidatesCart] = useState([]);
  const [approvedCandidatesForRequest, setApprovedCandidatesForRequest] = useState([]);

  // Occupation stats modal state
  const [showOccupationModal, setShowOccupationModal] = useState(false);

  // Messages state (now handled externally in MessagesPage, keeping minimal hook if needed)
  const [selectedOccupationStats, setSelectedOccupationStats] = useState(null);
  const [occupationCandidates, setOccupationCandidates] = useState([]);

  // Helper function to get the correct photo URL
  const getPhotoUrl = (photoUrl, updatedAt) => {
    if (!photoUrl) return null;

    // If it's a data URI (base64), return as is
    if (photoUrl.startsWith('data:')) {
      return photoUrl;
    }

    // Get base URL - use environment variable or construct from current origin
    let baseUrl = process.env.REACT_APP_API_URL;

    // If no env var, construct from current window location
    if (!baseUrl) {
      const origin = window.location.origin;
      // Replace port 3000 with 8080 for backend
      baseUrl = origin.replace(':3000', ':8080');
    }

    // Remove /api from the end or middle of the URL
    if (baseUrl.endsWith('/api')) {
      baseUrl = baseUrl.slice(0, -4);
    } else if (baseUrl.includes('/api/')) {
      baseUrl = baseUrl.replace('/api/', '/');
    } else if (baseUrl.endsWith('/api/')) {
      baseUrl = baseUrl.slice(0, -5);
    }

    baseUrl = baseUrl.replace(/\/$/, '');
    const normalizedPhotoUrl = photoUrl.startsWith('/') ? photoUrl : `/${photoUrl}`;

    let fullUrl = `${baseUrl}${normalizedPhotoUrl}`;

    if (updatedAt) {
      const timestamp = new Date(updatedAt).getTime();
      fullUrl += `?t=${timestamp}`;
    }

    return fullUrl;
  };

  // Fetch all users for statistics (without filters)
  const fetchAllUsersForStats = useCallback(async () => {
    try {
      const response = await api.get('/staff/users');
      console.log('[StaffDashboard] Fetched all users for stats:', response.data);

      let allUsers = [];
      if (Array.isArray(response.data)) {
        allUsers = response.data;
      } else if (response.data?.users) {
        allUsers = response.data.users;
      }

      console.log('[StaffDashboard] Total users for stats:', allUsers.length);

      // Calculate stats from all users
      setStats({
        pending: allUsers.filter(u => u.approval_status === 'pending' && u.role !== 'staff' && u.role !== 'admin').length,
        approved: allUsers.filter(u => u.approval_status === 'approved' && u.role !== 'staff' && u.role !== 'admin').length,
        rejected: allUsers.filter(u => u.approval_status === 'rejected' && u.role !== 'staff' && u.role !== 'admin').length,
        candidates: allUsers.filter(u => u.role === 'candidate').length,
        recruiters: allUsers.filter(u => u.role === 'recruiting_partner').length
      });
    } catch (error) {
      console.error('[StaffDashboard] Error fetching all users for stats:', error);
      setStats({
        pending: 0,
        approved: 0,
        rejected: 0,
        candidates: 0,
        recruiters: 0
      });
    }
  }, []);

  const fetchUsers = useCallback(async (pageNumber = 1) => {
    // Only set loading for tabs that actually need user list
    if (activeTab === 'approvals') {
      setLoading(true);
    }
    try {
      const params = {};
      if (activeTab === 'approvals') {
        if (activeApprovalTab === 'pending') {
          params.status = 'pending';
        } else if (activeApprovalTab === 'approved') {
          params.status = 'approved';
        } else if (activeApprovalTab === 'rejected') {
          params.status = 'rejected';
        }
        params.page = pageNumber;
        params.limit = 10;
      } else {
        // Fetch all users for other tabs if they rely on this
        params.limit = 10000;
      }
      if (roleFilter !== 'all') {
        params.role = roleFilter;
      }

      const queryString = new URLSearchParams(params).toString();
      const url = `/staff/users${queryString ? `?${queryString}` : ''}`;
      const response = await api.get(url);

      let allUsers = [];
      if (Array.isArray(response.data)) {
        allUsers = response.data;
      } else if (response.data?.users) {
        allUsers = response.data.users;
        
        // Update pagination state if present in the response
        if (response.data.page) {
          setCurrentPage(response.data.page);
          setTotalPages(response.data.total_pages);
          setTotalApprovals(response.data.total);
        }
      }

      // Filter out staff and admin users from staff dashboard
      const filteredResults = allUsers.filter(u => u.role !== 'staff' && u.role !== 'admin');
      setUsers(filteredResults);

      console.log('[StaffDashboard] Processed users count:', allUsers.length);
    } catch (error) {
      console.error('[StaffDashboard] Error fetching users:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
      console.error('[StaffDashboard] Error details:', errorMessage);
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      if (activeTab === 'approvals') {
        setLoading(false);
      }
    }
  }, [activeTab, activeApprovalTab, roleFilter]);

  const fetchInterviews = useCallback(async () => {
    try {
      const response = await api.get('/api/interviews');
      setInterviews(response.data || []);
    } catch (error) {
      console.error('Error fetching interviews:', error);
      showToast('Failed to fetch interviews');
    }
  }, [showToast]);

  const handleScheduleInterview = async (data) => {
    try {
      await api.post('/api/interviews', data);
      showToast('Interview scheduled successfully');
      setShowScheduleInterviewModal(false);
      fetchInterviews();
    } catch (error) {
      console.error('Error scheduling interview:', error);
      showToast(error.response?.data?.error || 'Failed to schedule interview');
    }
  };


  // Persist activeTab and activeApprovalTab to localStorage whenever they change


  useEffect(() => {
    localStorage.setItem('staffDashboard_activeApprovalTab', activeApprovalTab);
  }, [activeApprovalTab]);

  // Fetch data based on active tab - runs on mount and when tab changes
  useEffect(() => {
    // Use setTimeout to ensure this runs after initial render
    const timer = setTimeout(() => {
      try {
        if (activeTab === 'overview') {
          // Overview tab - fetch stats
          fetchAllUsersForStats();
          fetchCandidateStatistics();
          fetchManualPools();
          fetchUsers(); // Fix: Fetch users so they are available for manual pool candidate selection
        } else if (activeTab === 'approvals') {
          fetchUsers();
        } else if (activeTab === 'interviews') {
          fetchInterviews();
          fetchUsers();
        } else if (activeTab === 'recruiter-visibility') {
          fetchRecruiters();
          fetchAllCandidates();
          fetchCandidateStatistics(); // Fetch statistics for the Candidate Pool by Occupation section
          // Reset request candidates view when switching to visibility tab
          setShowRequestCandidates(false);
          setSelectedRequest(null);
        } else if (activeTab === 'recruiter-requests') {
          fetchRecruiterRequests();
        } else if (activeTab === 'recruiter-interests') {
          fetchRecruiterInterests(interestStatusFilter, interestTypeFilter);
        } else if (activeTab === 'notifications') {
          fetchNotifications();
        } else if (activeTab === 'sourcing-partners') {
          fetchSourcingPartners();
          // Always fetch all candidates when showing the "All Candidates per sourcing partner" view
          if (!showPartnerCandidates) {
            fetchSourcingCandidates();
          }
        } else if (activeTab === 'partner-feedbacks' || activeTab === 'recruiter-feedbacks' || activeTab === 'sourcing-feedbacks') {
          fetchPartnerFeedbacks();
        }
      } catch (error) {
        console.error('[StaffDashboard] Error in useEffect:', error);
      }
    }, 0);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activeApprovalTab, roleFilter, showPartnerCandidates]);

  // Check URL params for pre-selected recruiter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const recruiterId = urlParams.get('recruiterId');
    const tab = urlParams.get('tab');
    if (recruiterId && tab === 'recruiter-visibility') {
      navigate('/staff/recruiter-visibility');
      // Will be set after recruiters are fetched
    }
  }, []);

  const fetchRecruiters = async () => {
    try {
      const response = await api.get('/staff/users?role=recruiting_partner');
      // Handle both array and object responses
      let recruitersList = [];
      if (Array.isArray(response.data)) {
        recruitersList = response.data;
      } else if (response.data?.users) {
        recruitersList = response.data.users;
      }
      setRecruiters(recruitersList);
      // Check if recruiterId is in URL
      const urlParams = new URLSearchParams(window.location.search);
      const recruiterId = urlParams.get('recruiterId');
      if (recruiterId) {
        const recruiter = recruitersList.find(r => r.id === parseInt(recruiterId));
        if (recruiter) {
          setSelectedRecruiter(recruiter);
          fetchVisibleCandidates(recruiter.id);
        }
      }
    } catch (error) {
      console.error('[StaffDashboard] Error fetching recruiters:', error);
      setRecruiters([]);
    }
  };

  const fetchAllCandidates = async () => {
    try {
      // Fetch all candidates (both approved and pending) so staff can approve them for recruiters
      const response = await api.get('/staff/users?role=candidate');
      console.log('[StaffDashboard] Fetched all candidates:', response.data);
      // Handle both array and object responses
      let candidatesList = [];
      if (Array.isArray(response.data)) {
        candidatesList = response.data;
      } else if (response.data?.users) {
        candidatesList = response.data.users;
      }
      // Filter to only show approved candidates (staff can only grant visibility to approved candidates)
      const approvedCandidates = candidatesList.filter(c => c.approval_status === 'approved');
      console.log('[StaffDashboard] Approved candidates count:', approvedCandidates.length);
      setAllCandidatesForVisibility(approvedCandidates);
    } catch (error) {
      console.error('[StaffDashboard] Error fetching candidates:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
      console.error('Error details:', errorMessage);
      setAllCandidatesForVisibility([]);
    }
  };

  const fetchVisibleCandidates = async (recruiterId) => {
    try {
      const response = await api.get(`/staff/recruiters/${recruiterId}/candidates`);
      console.log('[StaffDashboard] Fetched visible candidates response:', response.data);
      // Handle both array and object responses
      let candidatesList = [];
      if (Array.isArray(response.data)) {
        candidatesList = response.data;
      } else if (response.data?.candidates) {
        candidatesList = response.data.candidates;
      }
      console.log('[StaffDashboard] Visible candidates count:', candidatesList.length);
      setVisibleCandidates(candidatesList);
    } catch (error) {
      console.error('[StaffDashboard] Error fetching visible candidates:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
      console.error('Error details:', errorMessage);
      setVisibleCandidates([]);
    }
  };

  const fetchRecruiterRequests = async () => {
    try {
      const response = await api.get('/staff/recruiter-requests');
      console.log('[StaffDashboard] Fetched recruiter requests:', response.data);
      setRecruiterRequests(response.data.requests || []);
    } catch (error) {
      console.error('[StaffDashboard] Error fetching recruiter requests:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
      console.error('Error details:', errorMessage);
      setRecruiterRequests([]);
    }
  };

  const fetchRecruiterInterests = async (statusFilter = null, typeFilter = null) => {
    try {
      // Don't set main loading state - this is a secondary data fetch
      const params = new URLSearchParams();
      const status = statusFilter !== null ? statusFilter : interestStatusFilter;
      const type = typeFilter !== null ? typeFilter : interestTypeFilter;

      if (status !== 'all') {
        params.append('status', status);
      }
      if (type !== 'all') {
        params.append('type', type);
      }
      const url = `/staff/recruiter-interests${params.toString() ? `?${params.toString()}` : ''}`;
      console.log('[StaffDashboard] Fetching recruiter interests from:', url);
      const response = await api.get(url);
      console.log('[StaffDashboard] Fetched recruiter interests response:', response.data);

      // Handle different response structures
      let interests = [];
      if (Array.isArray(response.data)) {
        interests = response.data;
      } else if (response.data?.interests) {
        interests = response.data.interests;
      } else if (response.data?.data) {
        interests = Array.isArray(response.data.data) ? response.data.data : [];
      }

      console.log('[StaffDashboard] Processed interests:', interests.length);
      setRecruiterInterests(interests);
    } catch (error) {
      console.error('[StaffDashboard] Error fetching recruiter interests:', error);
      console.error('[StaffDashboard] Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
      console.error('Error details:', errorMessage);
      setRecruiterInterests([]);
      // Don't show alert for every error, just log it
      console.warn('Failed to fetch recruiter interests:', errorMessage);
    }
  };

  const handleAckInterest = async (interestId) => {
    try {
      console.log('[StaffDashboard] Acknowledging interest:', interestId);
      const response = await api.put(`/staff/recruiter-interests/${interestId}/ack`);
      console.log('[StaffDashboard] Ack response:', response.data);

      // If currently filtering by 'pending', switch to 'acked' to show the newly acked item
      const newStatusFilter = interestStatusFilter === 'pending' ? 'acked' : interestStatusFilter;
      if (newStatusFilter !== interestStatusFilter) {
        setInterestStatusFilter(newStatusFilter);
      }

      // Refresh the list with the new filter to show updated status
      await fetchRecruiterInterests(newStatusFilter, interestTypeFilter);

      // No alert - UI will update automatically
    } catch (error) {
      console.error('[StaffDashboard] Error acknowledging interest:', error);
      console.error('[StaffDashboard] Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to acknowledge interest';
      showToast(`Failed to acknowledge interest: ${errorMessage}`);
    }
  };

  const handleMarkInterestClosed = async (interestId) => {
    try {
      console.log('[StaffDashboard] Marking interest as closed:', interestId);
      const response = await api.put(`/staff/recruiter-interests/${interestId}/close`);
      console.log('[StaffDashboard] Close response:', response.data);

      // If currently filtering by 'acked', switch to 'closed' to show the newly closed item
      const newStatusFilter = interestStatusFilter === 'acked' ? 'closed' : interestStatusFilter;
      if (newStatusFilter !== interestStatusFilter) {
        setInterestStatusFilter(newStatusFilter);
      }

      // Refresh the list with the new filter to show updated status
      await fetchRecruiterInterests(newStatusFilter, interestTypeFilter);

      // No alert - UI will update automatically
    } catch (error) {
      console.error('[StaffDashboard] Error marking interest as closed:', error);
      console.error('[StaffDashboard] Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to mark interest as closed';
      showToast(`Failed to mark interest as closed: ${errorMessage}`);
    }
  };

  const fetchManualPools = useCallback(async () => {
    setLoadingPools(true);
    try {
      const response = await api.get('/staff/manual-pools');
      setManualPools(response.data.pools || []);
    } catch (error) {
      console.error('[StaffDashboard] Error fetching manual pools:', error);
    } finally {
      setLoadingPools(false);
    }
  }, []);

  const handleCreatePool = async () => {
    if (!newPoolName.trim()) return;
    try {
      await api.post('/staff/manual-pools', {
        name: newPoolName,
        is_public: newPoolIsPublic
      });
      setNewPoolName('');
      setNewPoolIsPublic(false);
      setShowCreatePoolModal(false);
      fetchManualPools();
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to create pool');
    }
  };

  const handleTogglePoolVisibility = async (pool, currentVisibility) => {
    if (!pool.is_editable) return;
    try {
      await api.put(`/staff/manual-pools/${pool.id}`, {
        name: pool.name,
        is_public: !currentVisibility
      });
      fetchManualPools();
    } catch (error) {
      showToast('Failed to update pool visibility');
    }
  };

  const handleDeletePool = async (id) => {
    if (!window.confirm('Are you sure you want to delete this pool?')) return;
    try {
      await api.delete(`/staff/manual-pools/${id}`);
      fetchManualPools();
    } catch (error) {
      showToast('Failed to delete pool');
    }
  };

  const handleAddCandidateToPool = async (candidate) => {
    if (!selectedPool || !candidate) return;
    try {
      await api.post(`/staff/manual-pools/${selectedPool.id}/candidates`, {
        candidate_ids: [candidate.id]
      });
      fetchPoolCandidates(selectedPool);
      fetchManualPools();
      setShowSelectCandidateModal(false);
    } catch (error) {
      console.error(error);
      showToast('Failed to add candidate: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const fetchPoolCandidates = async (pool) => {
    setSelectedPool(pool);
    setLoadingPools(true);
    try {
      const source = pool.source || 'staff';
      const response = await api.get(`/staff/manual-pools/${pool.id}/candidates?source=${source}`);
      setPoolCandidates(response.data.candidates || []);
      setShowPoolCandidatesModal(true);
    } catch (error) {
      console.error('Error fetching pool candidates:', error);
    } finally {
      setLoadingPools(false);
    }
  };

  const handleAddToPool = async (poolId) => {
    if (!candidateToAddToPool) return;
    try {
      await api.post(`/staff/manual-pools/${poolId}/candidates`, {
        candidate_ids: [candidateToAddToPool.id]
      });
      setShowAddToPoolModal(false);
      setCandidateToAddToPool(null);
      fetchManualPools();
      // showToast('Candidate added to pool successfully'); // Removed excessive success toast
    } catch (error) {
      showToast('Failed to add candidate to pool');
    }
  };

  const handleRemoveFromPool = async (candidateId) => {
    if (!selectedPool) return;
    try {
      await api.delete(`/staff/manual-pools/${selectedPool.id}/candidates/${candidateId}`);
      // Refresh candidates list
      const source = selectedPool.source || 'staff';
      const response = await api.get(`/staff/manual-pools/${selectedPool.id}/candidates?source=${source}`);
      setPoolCandidates(response.data.candidates || []);
      fetchManualPools();
    } catch (error) {
      showToast('Failed to remove candidate');
    }
  };

  const fetchCandidateStatistics = async () => {
    try {
      const response = await api.get('/staff/candidate-statistics');
      console.log('[StaffDashboard] Fetched candidate statistics:', response.data);
      setCandidateStatistics(response.data.statistics || []);
      setTotalCandidates(response.data.total_candidates || 0);
    } catch (error) {
      console.error('[StaffDashboard] Error fetching candidate statistics:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
      console.error('Error details:', errorMessage);
      setCandidateStatistics([]);
      setTotalCandidates(0);
    }
  };



  const handleGrantVisibility = async (recruiterId, candidateId) => {
    try {
      await api.post(`/staff/recruiters/${recruiterId}/candidates`, { candidate_ids: [candidateId] });
      await Promise.all([
        fetchVisibleCandidates(recruiterId),
        fetchRecruiterRequests(),
        fetchAllCandidates()
      ]);
      // Success - no alert needed, UI will update automatically
    } catch (error) {
      console.error('[StaffDashboard] Error granting visibility:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to grant visibility';
      showToast(`Failed to grant visibility: ${errorMessage}`);
    }
  };

  const handleRevokeVisibility = async (recruiterId, candidateId) => {
    try {
      await api.delete(`/staff/recruiters/${recruiterId}/candidates/${candidateId}`);
      await Promise.all([
        fetchVisibleCandidates(recruiterId),
        fetchAllCandidates()
      ]);
      // showToast('Visibility revoked successfully'); // Removed success alert
    } catch (error) {
      console.error('[StaffDashboard] Error revoking visibility:', error);
      showToast('Failed to revoke visibility');
    }
  };

  const handleToggleContactUnmask = async (recruiterId, candidateId, currentStatus) => {
    try {
      await api.put(`/staff/recruiters/${recruiterId}/candidates/${candidateId}/unmask`, {
        unmasked: !currentStatus
      });
      // Refresh visible candidates to get the updated unmasked status
      await fetchVisibleCandidates(recruiterId);
    } catch (error) {
      console.error('[StaffDashboard] Error toggling contact unmask:', error);
      showToast('Failed to toggle contact unmask');
    }
  };

  const handleManageCandidates = async (request) => {
    try {
      console.log('[StaffDashboard] handleManageCandidates called with request:', request);
      // Ensure we're on the recruiter-requests tab
      navigate('/staff/recruiter-requests');
      setSelectedRequest(request);
      setShowRequestCandidates(true);

      // Reset visible candidates first
      setVisibleCandidates([]);
      setAllCandidatesForVisibility([]);
      setSelectedCandidatesCart([]);
      setApprovedCandidatesForRequest([]);
      setCandidateSearchTerm('');
      setSelectedOccupationFilter('all');

      // Always fetch fresh data sequentially to ensure proper state updates
      await fetchAllCandidates();
      await fetchRecruiters();

      if (request.recruiter_id) {
        await fetchVisibleCandidates(request.recruiter_id);
        // Load already approved candidates for this request
        try {
          const visible = await api.get(`/staff/recruiters/${request.recruiter_id}/candidates`);
          const visibleList = Array.isArray(visible.data) ? visible.data : (visible.data?.candidates || []);
          // Filter to only show candidates linked to this specific request (if request_id is available)
          const approvedForThisRequest = visibleList.filter(c =>
            !c.request_id || c.request_id === request.id
          );
          setApprovedCandidatesForRequest(approvedForThisRequest);
        } catch (err) {
          console.error('[StaffDashboard] Error loading approved candidates:', err);
        }
      } else {
        console.error('Request missing recruiter_id:', request);
        showToast('Error: Request missing recruiter information');
      }
    } catch (error) {
      console.error('[StaffDashboard] Error in handleManageCandidates:', error);
      showToast('Failed to load candidate management view');
    }
  };

  const handleOccupationClick = async (occupation) => {
    try {
      if (!occupation) return;

      console.log('[StaffDashboard] handleOccupationClick:', occupation);
      setLoading(true);
      setShowOccupationModal(true);
      setSelectedOccupationStats(occupation);
      setOccupationCandidates([]); // Clear previous

      // If we already have list of all approved candidates (from fetchAllCandidates), 
      // we can filter client-side. Otherwise we might want to fetch.
      // Usually fetchAllCandidates() is called when mounting 'recruiter-visibility' tab.
      // But let's be safe and fetch it if needed or ensure we have it.

      // Let's try to fetch fresh list of all candidates to ensure we have occupations
      const response = await api.get('/staff/users?role=candidate');
      let candidatesList = [];
      if (Array.isArray(response.data)) {
        candidatesList = response.data;
      } else if (response.data?.users) {
        candidatesList = response.data.users;
      }

      // Filter for approved candidates only, as usually analytics counts approved ones
      // And filter by occupation
      const filtered = candidatesList.filter(c =>
        c.approval_status === 'approved' &&
        (c.occupation === occupation || (!c.occupation && occupation === 'Not Specified'))
      );

      console.log(`[StaffDashboard] Found ${filtered.length} candidates for ${occupation}`);
      setOccupationCandidates(filtered);
    } catch (error) {
      console.error('[StaffDashboard] Error fetching occupation candidates:', error);
      showToast('Failed to load candidates for this occupation');
      setShowOccupationModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!requestToReject || !rejectReason.trim()) return;

    setRejectingRequest(true);
    try {
      await api.put(`/staff/recruiter-requests/${requestToReject.id}/status`, {
        status: 'rejected',
        admin_comment: rejectReason.trim()
      });

      await fetchRecruiterRequests();

      setShowRejectRequestModal(false);
      setRequestToReject(null);
      setRejectReason('');
    } catch (error) {
      console.error('Error rejecting request:', error);
      showToast('Failed to reject request. Please try again.');
    } finally {
      setRejectingRequest(false);
    }
  };

  const handleApproveCandidateForRequest = async (candidateIds) => {
    if (!selectedRequest) {
      console.error('[StaffDashboard] No selected request');
      showToast('Error: No request selected');
      return;
    }

    // Ensure candidateIds is an array
    const idsToApprove = Array.isArray(candidateIds) ? candidateIds : [candidateIds];

    if (idsToApprove.length === 0) {
      showToast('Please select at least one candidate to approve');
      return;
    }

    console.log('[StaffDashboard] Approving candidates', idsToApprove, 'for request', selectedRequest.id, 'recruiter', selectedRequest.recruiter_id);

    try {
      const response = await api.post(`/staff/recruiters/${selectedRequest.recruiter_id}/candidates`, {
        candidate_ids: idsToApprove,
        request_id: selectedRequest.id
      });
      console.log('[StaffDashboard] Approve response:', response.data);

      // Refresh the lists
      await Promise.all([
        fetchAllCandidates(),
        fetchVisibleCandidates(selectedRequest.recruiter_id),
        fetchRecruiterRequests()
      ]);

      // Refresh approved candidates list for this request
      if (selectedRequest.recruiter_id) {
        try {
          const visible = await api.get(`/staff/recruiters/${selectedRequest.recruiter_id}/candidates`);
          const visibleList = Array.isArray(visible.data) ? visible.data : (visible.data?.candidates || []);
          const approvedForThisRequest = visibleList.filter(c =>
            !c.request_id || c.request_id === selectedRequest.id
          );
          setApprovedCandidatesForRequest(approvedForThisRequest);
        } catch (err) {
          console.error('[StaffDashboard] Error refreshing approved candidates:', err);
        }
      }

      // Clear cart
      setSelectedCandidatesCart([]);

      // showToast(`Successfully approved ${idsToApprove.length} candidate(s)`); // Removed success alert
    } catch (error) {
      console.error('[StaffDashboard] Error approving candidates:', error);
      console.error('[StaffDashboard] Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to approve candidates';
      showToast(`Failed to approve candidates: ${errorMessage}`);
    }
  };

  const handleRemoveCandidateFromRequest = async (candidateId) => {
    if (!selectedRequest) {
      console.error('[StaffDashboard] No selected request');
      showToast('Error: No request selected');
      return;
    }

    if (!window.confirm('Are you sure you want to remove this candidate from the approved list?')) {
      return;
    }

    console.log('[StaffDashboard] Removing candidate', candidateId, 'from request', selectedRequest.id, 'recruiter', selectedRequest.recruiter_id);

    try {
      await api.delete(`/staff/recruiters/${selectedRequest.recruiter_id}/candidates/${candidateId}`);
      console.log('[StaffDashboard] Candidate removed successfully');

      // Refresh the lists
      await Promise.all([
        fetchAllCandidates(),
        fetchVisibleCandidates(selectedRequest.recruiter_id),
        fetchRecruiterRequests()
      ]);

      // Refresh approved candidates list for this request
      if (selectedRequest.recruiter_id) {
        try {
          const visible = await api.get(`/staff/recruiters/${selectedRequest.recruiter_id}/candidates`);
          const visibleList = Array.isArray(visible.data) ? visible.data : (visible.data?.candidates || []);
          const approvedForThisRequest = visibleList.filter(c =>
            !c.request_id || c.request_id === selectedRequest.id
          );
          setApprovedCandidatesForRequest(approvedForThisRequest);
        } catch (err) {
          console.error('[StaffDashboard] Error refreshing approved candidates:', err);
        }
      }

      // showToast('Candidate removed successfully'); // Removed success alert
    } catch (error) {
      console.error('[StaffDashboard] Error removing candidate:', error);
      console.error('[StaffDashboard] Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to remove candidate';
      showToast(`Failed to remove candidate: ${errorMessage}`);
    }
  };

  const toggleCandidateInCart = (candidateId) => {
    setSelectedCandidatesCart(prev => {
      if (prev.includes(candidateId)) {
        return prev.filter(id => id !== candidateId);
      } else {
        return [...prev, candidateId];
      }
    });
  };

  const getOccupationCategories = () => {
    const occupations = new Set();
    allCandidatesForVisibility.forEach(candidate => {
      if (candidate.occupation) {
        occupations.add(candidate.occupation);
      } else {
        occupations.add('Not Specified');
      }
    });
    return Array.from(occupations).sort();
  };

  const getFilteredCandidates = () => {
    let filtered = allCandidatesForVisibility;

    // Filter by occupation
    if (selectedOccupationFilter !== 'all') {
      if (selectedOccupationFilter === 'Not Specified') {
        filtered = filtered.filter(c => !c.occupation);
      } else {
        filtered = filtered.filter(c => c.occupation === selectedOccupationFilter);
      }
    }

    // Filter by search term
    if (candidateSearchTerm.trim()) {
      const searchLower = candidateSearchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        c.name?.toLowerCase().includes(searchLower) ||
        c.email?.toLowerCase().includes(searchLower) ||
        c.qualification?.toLowerCase().includes(searchLower) ||
        c.occupation?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  };

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/staff/notifications');
      const notifs = Array.isArray(response.data) ? response.data : [];
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('[StaffDashboard] Error fetching notifications:', error);
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  const fetchSourcingCandidates = async () => {
    try {
      const params = new URLSearchParams();
      if (sourcingStatusFilter && sourcingStatusFilter !== 'all') {
        params.append('status', sourcingStatusFilter);
      }
      const url = `/staff/sourcing-candidates${params.toString() ? `?${params.toString()}` : ''}`;
      console.log('[StaffDashboard] Fetching sourcing candidates from:', url);
      const response = await api.get(url);
      console.log('[StaffDashboard] Fetched sourcing candidates:', response.data);
      console.log('[StaffDashboard] Number of candidates:', Array.isArray(response.data) ? response.data.length : 'not an array');

      // Ensure we're setting an array
      if (Array.isArray(response.data)) {
        setSourcingCandidates(response.data);
      } else if (response.data && typeof response.data === 'object') {
        // If response.data is an object, try to extract an array from it
        console.warn('[StaffDashboard] Response data is not an array:', response.data);
        setSourcingCandidates([]);
      } else {
        setSourcingCandidates([]);
      }
    } catch (error) {
      console.error('[StaffDashboard] Error fetching sourcing candidates:', error);
      console.error('[StaffDashboard] Error response:', error.response);
      console.error('[StaffDashboard] Error details:', error.response?.data);
      console.error('[StaffDashboard] Error status:', error.response?.status);
      setSourcingCandidates([]);
    }
  };

  const fetchSourcingPartners = async () => {
    try {
      const response = await api.get('/staff/sourcing-partners');
      setSourcingPartners(response.data || []);
    } catch (error) {
      console.error('[StaffDashboard] Error fetching sourcing partners:', error);
      setSourcingPartners([]);
    }
  };

  const fetchPartnerCandidates = async (partnerId) => {
    try {
      const response = await api.get(`/staff/sourcing-partners/${partnerId}/candidates`);
      setSourcingCandidates(response.data || []);
    } catch (error) {
      console.error('[StaffDashboard] Error fetching partner candidates:', error);
      setSourcingCandidates([]);
    }
  };

  const handleSelectSourcingPartner = async (partner) => {
    setSelectedSourcingPartner(partner);
    setShowPartnerCandidates(true);
    await fetchPartnerCandidates(partner.id);
  };

  const handleViewAllCandidates = async () => {
    setSelectedSourcingPartner(null);
    setShowPartnerCandidates(false);
    await fetchSourcingCandidates();
  };

  const handleApproveSourcingCandidate = async (candidateId) => {
    try {
      await api.put(`/staff/sourcing-candidates/${candidateId}/approve`);
      if (showPartnerCandidates && selectedSourcingPartner) {
        await fetchPartnerCandidates(selectedSourcingPartner.id);
      } else {
        await fetchSourcingCandidates();
      }
      await fetchSourcingPartners(); // Refresh partner counts
      // showToast('Candidate approved successfully'); // Removed success alert
    } catch (error) {
      console.error('[StaffDashboard] Error approving sourcing candidate:', error);
      showToast('Failed to approve candidate');
    }
  };

  const handleRejectSourcingCandidate = async (candidateId, reason = '') => {
    try {
      await api.put(`/staff/sourcing-candidates/${candidateId}/reject`, { reason });
      if (showPartnerCandidates && selectedSourcingPartner) {
        await fetchPartnerCandidates(selectedSourcingPartner.id);
      } else {
        await fetchSourcingCandidates();
      }
      await fetchSourcingPartners(); // Refresh partner counts
      // showToast('Candidate rejected successfully'); // This is a rejection, not a success. Keeping it.
    } catch (error) {
      console.error('[StaffDashboard] Error rejecting sourcing candidate:', error);
      showToast('Failed to reject candidate');
    }
  };

  const fetchPartnerFeedbacks = async () => {
    try {
      const params = new URLSearchParams();
      if (feedbackStatusFilter !== 'all') params.append('status', feedbackStatusFilter);
      if (feedbackPartnerTypeFilter !== 'all') params.append('partner_type', feedbackPartnerTypeFilter);

      const response = await api.get(`/staff/partner-feedbacks?${params.toString()}`);
      setPartnerFeedbacks(response.data || []);
    } catch (error) {
      console.error('[StaffDashboard] Error fetching partner feedbacks:', error);
      setPartnerFeedbacks([]);
    }
  };

  const fetchFeedbackResponses = async (feedbackId) => {
    try {
      const response = await api.get(`/staff/partner-feedbacks/${feedbackId}/responses`);
      setFeedbackResponses(response.data || []);
    } catch (error) {
      console.error('[StaffDashboard] Error fetching feedback responses:', error);
      setFeedbackResponses([]);
    }
  };

  const handleViewFeedback = async (feedback) => {
    setSelectedFeedback(feedback);
    setShowFeedbackModal(true);
    await fetchFeedbackResponses(feedback.id);

    // Mark as read if it's new
    if (feedback.status === 'new') {
      try {
        await api.put(`/staff/partner-feedbacks/${feedback.id}/status`, { status: 'read' });
        await fetchPartnerFeedbacks();
      } catch (error) {
        console.error('Error marking feedback as read:', error);
      }
    }
  };

  const handleRespondToFeedback = async () => {
    if (!responseText.trim()) {
      showToast('Please enter a response');
      return;
    }

    try {
      await api.post(`/staff/partner-feedbacks/${selectedFeedback.id}/respond`, {
        response_text: responseText
      });
      setResponseText('');
      await fetchFeedbackResponses(selectedFeedback.id);
      await fetchPartnerFeedbacks();
    } catch (error) {
      console.error('Error submitting response:', error);
      showToast('Failed to submit response. Please try again.');
    }
  };

  const handleUpdateFeedbackStatus = async (feedbackId, newStatus) => {
    try {
      await api.put(`/staff/partner-feedbacks/${feedbackId}/status`, { status: newStatus });
      await fetchPartnerFeedbacks();
      if (selectedFeedback && selectedFeedback.id === feedbackId) {
        setSelectedFeedback({ ...selectedFeedback, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating feedback status:', error);
      showToast('Failed to update status. Please try again.');
    }
  };

  const handleApproveInterest = async (notification, recruiterList = null, fetchRecruitersFn = null) => {
    try {
      // Parse notification message to extract recruiter email and candidate info
      // Message format: "Recruiting Partner/Employer {name} ({email}) has shown interest in candidate {name} ({email})"
      const message = notification.message;
      const recruiterMatch = message.match(/(?:Recruiting Partner|Employer) (.+?) \((.+?)\)/);
      const candidateMatch = message.match(/candidate (.+?) \((.+?)\)/);

      if (!recruiterMatch || !candidateMatch || !notification.reference_id) {
        showToast('Could not parse notification details');
        return false;
      }

      const recruiterEmail = recruiterMatch[2];
      const candidateId = notification.reference_id;

      // Use provided recruiter list or fetch if needed
      let recruiterListToUse = recruiterList || recruiters;
      if (recruiterListToUse.length === 0) {
        if (fetchRecruitersFn) {
          await fetchRecruitersFn();
          recruiterListToUse = recruiters;
        } else {
          await fetchRecruiters();
          recruiterListToUse = recruiters;
        }
      }

      const recruiter = recruiterListToUse.find(r => r.email === recruiterEmail);
      if (!recruiter) {
        showToast('Recruiter not found. Please try again.');
        return false;
      }

      // Grant visibility
      await api.post(`/staff/recruiters/${recruiter.id}/candidates`, {
        candidate_ids: [candidateId]
      });

      // Refresh notifications and requests
      if (activeTab === 'notifications') {
        await fetchNotifications();
      }
      await fetchRecruiterRequests();
      // Success - no alert needed, UI will update automatically
      return true;
    } catch (error) {
      console.error('[StaffDashboard] Error approving interest:', error);
      showToast('Failed to approve interest');
      return false;
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await api.put(`/staff/notifications/${notificationId}/read`);
      await fetchNotifications();
    } catch (error) {
      console.error('[StaffDashboard] Error marking notification as read:', error);
    }
  };

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter, activeApprovalTab, activeTab, languageSort]);

  const filterUsers = () => {
    if (!users || users.length === 0) {
      console.log('[StaffDashboard] filterUsers: No users to filter');
      setFilteredUsers([]);
      return;
    }

    let filtered = [...users];
    console.log('[StaffDashboard] filterUsers: Starting with', filtered.length, 'users');

    if (activeTab === 'approvals') {
      if (activeApprovalTab === 'pending') {
        filtered = filtered.filter(u => u.approval_status === 'pending');
        console.log('[StaffDashboard] filterUsers: After pending filter:', filtered.length);
      } else if (activeApprovalTab === 'approved') {
        filtered = filtered.filter(u => u.approval_status === 'approved');
        console.log('[StaffDashboard] filterUsers: After approved filter:', filtered.length);
      } else if (activeApprovalTab === 'rejected') {
        filtered = filtered.filter(u => u.approval_status === 'rejected');
        console.log('[StaffDashboard] filterUsers: After rejected filter:', filtered.length);
      }
    }

    if (roleFilter !== 'all') {
      const beforeRole = filtered.length;
      filtered = filtered.filter(u => u.role === roleFilter);
      console.log('[StaffDashboard] filterUsers: After role filter (' + roleFilter + '):', filtered.length, '(was', beforeRole + ')');
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const beforeSearch = filtered.length;
      filtered = filtered.filter(u =>
        u.name?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.role?.toLowerCase().includes(term)
      );
      console.log('[StaffDashboard] filterUsers: After search filter:', filtered.length, '(was', beforeSearch + ')');
    }

    if (languageSort !== 'none') {
      const levelMap = { 'A1': 1, 'A2': 2, 'B1': 3, 'B2': 4, 'C1': 5, 'C2': 6 };
      filtered.sort((a, b) => {
        const valA = levelMap[a.german_level] || 0;
        const valB = levelMap[b.german_level] || 0;
        return languageSort === 'desc' ? valB - valA : valA - valB;
      });
    }

    console.log('[StaffDashboard] filterUsers: Final filtered count:', filtered.length);
    setFilteredUsers(filtered);
  };

  const handleApprove = async (userId) => {
    try {
      await api.put(`/staff/users/${userId}/approval`, { status: 'approved' });
      await fetchUsers();
    } catch (error) {
      console.error('[StaffDashboard] Error approving user:', error);
      showToast('Failed to approve user');
    }
  };

  const handleReject = async (userId) => {
    try {
      await api.put(`/staff/users/${userId}/approval`, { status: 'rejected' });
      await fetchUsers();
    } catch (error) {
      console.error('[StaffDashboard] Error rejecting user:', error);
      showToast('Failed to reject user');
    }
  };

  const handleUpdateStatus = async (userId, status) => {
    try {
      await api.put(`/staff/users/${userId}/approval`, { status });
      await fetchUsers();
    } catch (error) {
      console.error('[StaffDashboard] Error updating user status:', error);
      showToast(`Failed to update user status to ${status}`);
    }
  };

  const handleEditUser = async (userData, shouldClose = true) => {
    try {
      // If userData is provided (from manual call), update via API
      // If userData is missing (callback from ProfileEditor which handles update internally), skip API call
      if (userData) {
        await api.put(`/staff/users/${selectedUser.id}`, userData);
      }

      await fetchUsers();
      await fetchAllUsersForStats(); // Refresh stats after edit

      if (shouldClose) {
        setShowUserModal(false);
        setShowEditModal(false);
      }
    } catch (error) {
      console.error('[StaffDashboard] Error updating user:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to update user';
      showToast(`Failed to update user: ${errorMessage}`);
    }
  };

  const handleViewUser = async (user) => {
    setUserDetailsLoading(true);
    setSelectedUser(user);
    // Navigate to profile page with full page reload
    window.location.href = `/staff/profile/${user.id}`;
    setUserDetailsLoading(false);
  };

  // If this is a sourcing partner candidate, mark it as viewed
  const markCandidateAsViewed = async (user) => {
    if (user.sourcing_partner_id && activeTab === 'sourcing-partners') {
      try {
        await api.put(`/staff/sourcing-candidates/${user.id}/viewed`);
        // Update local state to remove "new" badge
        setSourcingCandidates(prev => prev.map(c =>
          c.id === user.id ? { ...c, is_new: false } : c
        ));
      } catch (error) {
        console.error('Error marking candidate as viewed:', error);
      }
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'recruiting_partner':
        return <Briefcase className="h-5 w-5 text-purple-600" />;
      case 'sourcing_partner':
        return <Users className="h-4 w-4 text-orange-500" />;
      case 'candidate':
        return <GraduationCap className="h-5 w-5 text-orange-600" />;
      default:
        return <Users className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
            Approved
          </span>
        );
      case 'pending':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
            Pending
          </span>
        );
      case 'rejected':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
            Rejected
          </span>
        );
      default:
        return null;
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }

    try {
      if (passwordResetUserId) {
        // Staff is resetting another user's password
        await api.post(`/staff/users/${passwordResetUserId}/reset-password`, {
          password: newPassword
        });
        // showToast('User password reset successfully'); // Removed success alert
      } else {
        // Staff is resetting their own password
        await api.post('/auth/reset-password', {
          password: newPassword,
          token: localStorage.getItem('token')
        });
        // showToast('Password reset successfully'); // Removed success alert
      }
      setShowPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
      setPasswordResetUserId(null);
    } catch (error) {
      console.error('[StaffDashboard] Error resetting password:', error);
      setPasswordError(error.response?.data?.error || error.response?.data?.message || 'Failed to reset password');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    if (!createUserForm.name || !createUserForm.email || !createUserForm.password) {
      showToast('Please fill in all required fields');
      return;
    }

    // Prevent staff from creating admin or staff users
    if (createUserForm.role === 'admin' || createUserForm.role === 'staff') {
      showToast(`Cannot create ${createUserForm.role} user`);
      return;
    }

    try {
      await api.post('/staff/users', createUserForm);
      // showToast('User created successfully'); // Removed success alert
      setShowCreateModal(false);
      setCreateUserForm({
        name: '',
        email: '',
        password: '',
        role: 'candidate',
        approval_status: 'approved',
        profile_status: 'incomplete'
      });
      await fetchAllUsersForStats();
      if (activeTab === 'approvals') {
        await fetchUsers();
      }
    } catch (error) {
      console.error('[StaffDashboard] Error creating user:', error);
      showToast(error.response?.data?.error || 'Failed to create user');
    }
  };

  // Always render - never block
  console.log('[StaffDashboard] Rendering, activeTab:', activeTab, 'loading:', loading);

  return (
    <div className="min-h-screen bg-gray-50 flex overflow-hidden">

      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-30 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Modern Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-40 w-72 bg-slate-900 border-r border-slate-800 flex flex-col shadow-2xl transition-transform duration-300 ease-in-out md:relative md:translate-x-0 md:z-20 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b border-slate-800/50 bg-slate-900/50 backdrop-blur-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="h-10 w-10 bg-white rounded-xl flex items-center justify-center p-1 shadow-lg shadow-blue-500/10 border border-slate-700">
                  <img src="/dashboard_logo.png" alt="Polyglots" className="h-full w-full object-contain" />
                </div>
                <div className="absolute -bottom-1 -right-1 h-3 w-3 bg-emerald-500 rounded-full border-2 border-slate-900"></div>
              </div>
              <div>
                <h1 className="text-lg font-bold text-white tracking-tight">Polyglots</h1>
                <p className="text-xs text-slate-400 font-medium whitespace-nowrap">Staff Portal</p>
              </div>
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="md:hidden p-1.5 text-slate-400 hover:text-white rounded-lg transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-6 px-4 space-y-1 custom-scrollbar">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 px-2">Main Menu</p>

          <SidebarItem
            icon={Briefcase}
            label="Overview"
            active={activeTab === 'overview'}
            onClick={() => navigate('/staff/overview')}
          />

          <div className="pt-2"></div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 px-2 mt-4">Approvals & Users</p>

          <div className="space-y-1">
            <SidebarItem
              icon={UserCheck}
              label="Approvals"
              active={activeTab === 'approvals'}
              onClick={() => {
                setApprovalsMenuOpen(!approvalsMenuOpen);
                navigate('/staff/approvals');
              }}
              hasSubmenu={true}
              isOpen={approvalsMenuOpen}
              badge={stats.pending > 0 ? stats.pending : null}
            />

            {/* Submenu for Approvals */}
            <div className={`pl-4 space-y-1 overflow-hidden transition-all duration-300 ${approvalsMenuOpen ? 'max-h-40 opacity-100 mt-1' : 'max-h-0 opacity-0'}`}>
              <button
                onClick={() => {
                  navigate('/staff/approvals');
                  setActiveApprovalTab('pending');
                }}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors ${activeTab === 'approvals' && activeApprovalTab === 'pending'
                  ? 'text-blue-400 bg-blue-500/10 font-medium'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-1.5 h-1.5 rounded-full ${activeTab === 'approvals' && activeApprovalTab === 'pending' ? 'bg-blue-500' : 'bg-slate-600'}`}></div>
                  <span>Pending</span>
                </div>
                {stats.pending > 0 && <span className="text-[10px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded border border-amber-500/20">{stats.pending}</span>}
              </button>

              <button
                onClick={() => {
                  navigate('/staff/approvals');
                  setActiveApprovalTab('approved');
                }}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors ${activeTab === 'approvals' && activeApprovalTab === 'approved'
                  ? 'text-blue-400 bg-blue-500/10 font-medium'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-1.5 h-1.5 rounded-full ${activeTab === 'approvals' && activeApprovalTab === 'approved' ? 'bg-emerald-500' : 'bg-slate-600'}`}></div>
                  <span>Approved</span>
                </div>
              </button>

              <button
                onClick={() => {
                  navigate('/staff/approvals');
                  setActiveApprovalTab('rejected');
                }}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors ${activeTab === 'approvals' && activeApprovalTab === 'rejected'
                  ? 'text-blue-400 bg-blue-500/10 font-medium'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-1.5 h-1.5 rounded-full ${activeTab === 'approvals' && activeApprovalTab === 'rejected' ? 'bg-red-500' : 'bg-slate-600'}`}></div>
                  <span>Rejected</span>
                </div>
              </button>
            </div>
          </div>

          <div className="pt-2"></div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 px-2 mt-4">Recruitment</p>

          <SidebarItem
            icon={Eye}
            label="Recruiter Visibility"
            active={activeTab === 'recruiter-visibility'}
            onClick={() => navigate('/staff/recruiter-visibility')}
          />

          <SidebarItem
            icon={Calendar}
            label="Interviews"
            active={activeTab === 'interviews'}
            onClick={() => navigate('/staff/interviews')}
          />

          <SidebarItem
            icon={ShoppingCart}
            label="Recruiter Requests"
            active={activeTab === 'recruiter-requests'}
            onClick={() => navigate('/staff/recruiter-requests')}
            badge={recruiterRequests.filter(r => r.status === 'pending').length || null}
          />

          <SidebarItem
            icon={Briefcase}
            label="Recruiter Interests"
            active={activeTab === 'recruiter-interests'}
            onClick={() => {
              navigate('/staff/recruiter-interests');
              fetchRecruiterInterests(interestStatusFilter, interestTypeFilter);
            }}
            badge={recruiterInterests.filter(i => i.status === 'pending').length || null}
          />

          <SidebarItem
            icon={MessageSquare}
            label="Recruiter Feedbacks"
            active={activeTab === 'recruiter-feedbacks'}
            onClick={() => {
              navigate('/staff/recruiter-feedbacks');
              setFeedbackPartnerTypeFilter('recruiting_partner');
              fetchPartnerFeedbacks();
            }}
            badge={partnerFeedbacks.filter(f => f.status === 'new' && f.partner_type === 'recruiting_partner').length || null}
          />

          <SidebarItem
            icon={FileText}
            label="Recruiting Jobs"
            active={activeTab === 'recruiting-jobs'}
            onClick={() => navigate('/staff/recruiting-jobs')}
          />

          <div className="pt-2"></div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 px-2 mt-4">Sourcing</p>

          <SidebarItem
            icon={Users}
            label="Sourcing Partners"
            active={activeTab === 'sourcing-partners'}
            onClick={() => {
              navigate('/staff/sourcing-partners');
              setShowPartnerCandidates(false);
              setSelectedSourcingPartner(null);
              fetchSourcingPartners();
              fetchSourcingCandidates();
            }}
          />

          <SidebarItem
            icon={MessageSquare}
            label="Sourcing Feedbacks"
            active={activeTab === 'sourcing-feedbacks'}
            onClick={() => {
              navigate('/staff/sourcing-feedbacks');
              setFeedbackPartnerTypeFilter('sourcing_partner');
              fetchPartnerFeedbacks();
            }}
            badge={partnerFeedbacks.filter(f => f.status === 'new' && f.partner_type === 'sourcing_partner').length || null}
          />

          <div className="pt-2"></div>

          <SidebarItem
            icon={Bell}
            label="Notifications"
            active={activeTab === 'notifications'}
            onClick={() => {
              navigate('/staff/notifications');
              fetchNotifications();
            }}
            badge={unreadCount > 0 ? unreadCount : null}
          />

        </nav>

        <div className="p-4 border-t border-slate-800 bg-slate-900/50">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all group"
          >
            <LogOut className="h-5 w-5 group-hover:rotate-180 transition-transform duration-300" />
            <span className="font-medium text-sm">Sign Out</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col bg-slate-50 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-blue-50/50 to-transparent pointer-events-none" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-50/30 rounded-full blur-3xl pointer-events-none translate-x-1/2 -translate-y-1/2" />

        {/* Modern Header */}
        <header className="bg-white/80 backdrop-blur-md border-b border-gray-200/50 sticky top-0 z-20 shadow-sm">
          <div className="w-full px-4 md:px-8 py-4">
            <div className="flex items-center justify-between gap-4 md:gap-8">
              {/* Mobile Hamburger */}
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="md:hidden flex-shrink-0 p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <Menu size={22} />
              </button>
              {/* Search Bar */}
              <div className="relative flex-1 max-w-xl group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                </div>
                <input
                  type="text"
                  placeholder="Search candidates, requests..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-xl leading-5 bg-slate-50 text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <span className="text-xs text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">⌘K</span>
                </div>
              </div>

              {/* Right Actions */}
              <div className="flex items-center gap-4">
                {/* Notification Bell */}
                <NotificationBell />

                {/* Profile Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                    className="flex items-center gap-3 p-1.5 rounded-xl hover:bg-slate-100 transition-colors border border-transparent hover:border-slate-200"
                  >
                    <div className="h-9 w-9 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center text-white font-bold shadow-md shadow-blue-500/20">
                      {currentUser?.name ? currentUser.name.charAt(0).toUpperCase() : 'S'}
                    </div>
                    <div className="hidden md:block text-left mr-2">
                      <p className="text-sm font-bold text-slate-800 leading-tight">{currentUser?.name || 'Staff Member'}</p>
                      <p className="text-xs text-slate-500 font-medium">{currentUser?.email || 'staff@example.com'}</p>
                    </div>
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  </button>

                  {/* Dropdown Menu */}
                  {showProfileDropdown && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 ring-1 ring-black ring-opacity-5 py-2 transform opacity-100 scale-100 transition-all z-50 origin-top-right">
                      <div className="px-4 py-3 border-b border-gray-50 bg-gray-50/50">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Signed in as</p>
                        <p className="text-sm font-bold text-gray-900 truncate">{currentUser?.email}</p>
                      </div>

                      <div className="p-2">
                        <button
                          onClick={() => {
                            setShowProfileDropdown(false);
                            setPasswordResetUserId(currentUser?.id); // Reset own password
                            setShowPasswordModal(true);
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded-xl flex items-center gap-2 transition-colors"
                        >
                          <Key className="h-4 w-4" />
                          Change Password
                        </button>

                        <div className="my-1 border-t border-gray-100"></div>

                        <button
                          onClick={logout}
                          className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-xl flex items-center gap-2 transition-colors"
                        >
                          <LogOut className="h-4 w-4" />
                          Sign out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="p-8 overflow-y-auto flex-1 custom-scrollbar">
          {/* Recent Activity / Welcome Section */}
          {activeTab !== 'profile' && (
            <div className="mb-8">
              <div className="bg-gradient-to-br from-indigo-900 via-slate-800 to-slate-900 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl -ml-10 -mb-10 pointer-events-none"></div>

                <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="flex items-center gap-6">
                    <div className="h-20 w-20 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center shadow-xl">
                      <span className="text-3xl font-bold text-white">{currentUser?.name?.charAt(0) || 'S'}</span>
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold text-white mb-2">Welcome back, {currentUser?.name?.split(' ')[0]}!</h1>
                      <p className="text-blue-200 flex items-center gap-2">
                        <Briefcase className="h-4 w-4" />
                        <span>Staff Administrator Dashboard</span>
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/10 text-center">
                      <p className="text-xs text-blue-200 uppercase tracking-wider font-bold mb-1">Today's Date</p>
                      <p className="text-white font-mono font-bold">{new Date().toLocaleDateString()}</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/10 text-center">
                      <p className="text-xs text-blue-200 uppercase tracking-wider font-bold mb-1">Pending Actions</p>
                      <p className="text-white font-mono font-bold text-lg text-amber-400">{stats.pending + (recruiterRequests.filter(r => r.status === 'pending').length)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'profile' && (
            <CandidateProfileView candidate={null} isPageView={true} embedded={true} />
          )}


          {/* Overview Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-6 animate-fadeIn">
              {/* Local Filters Bar */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 flex flex-col md:flex-row gap-4 justify-between items-center">
                <div className="relative w-full md:w-96 group">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 h-5 w-5 group-focus-within:text-blue-500 transition-colors" />
                  <input
                    type="text"
                    placeholder="Search users by name, email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium text-slate-900"
                  />
                </div>

                <div className="flex w-full md:w-auto gap-3">
                  <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-medium text-slate-700 cursor-pointer"
                  >
                    <option value="all">All Roles</option>
                    <option value="candidate">Candidate</option>
                    <option value="recruiting_partner">Employer</option>
                    <option value="sourcing_partner">Sourcing Partner</option>
                  </select>
                </div>
              </div>

              {/* Matching Candidates Section (Show when searching) */}
              {searchTerm && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden shadow-xl shadow-blue-500/10 ring-1 ring-blue-500/10">
                  <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-blue-50/50">
                    <div>
                      <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <Users className="h-5 w-5 text-blue-600" />
                        Matching Users ({filteredUsers.length})
                      </h2>
                      <p className="text-sm text-slate-500 mt-1">Found matching your search term: "{searchTerm}"</p>
                    </div>
                    {filteredUsers.length > 0 && (
                      <span className="text-xs font-bold text-blue-600 uppercase tracking-widest px-3 py-1 bg-blue-100 rounded-full">
                        Results Found
                      </span>
                    )}
                  </div>

                  <div className="divide-y divide-slate-100">
                    {filteredUsers.length === 0 ? (
                      <div className="p-12 text-center text-slate-400">
                        <div className="mx-auto h-16 w-16 bg-slate-50 rounded-full flex items-center justify-center mb-3 border-2 border-dashed border-slate-200">
                          <Search className="h-8 w-8 text-slate-300" />
                        </div>
                        <p className="font-medium">No users found matching "{searchTerm}"</p>
                      </div>
                    ) : (
                      filteredUsers.slice(0, 10).map((u) => (
                        <div key={u.id}
                          className="p-5 hover:bg-blue-50/50 transition-all cursor-pointer group flex items-center justify-between border-l-4 border-transparent hover:border-blue-500"
                          onClick={() => handleViewUser(u)}>
                          <div className="flex items-center gap-4">
                            <div className="h-12 w-12 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200 relative shadow-sm group-hover:scale-105 transition-transform">
                              {u.photo_url ? (
                                <img src={getPhotoUrl(u.photo_url, u.updated_at)} alt={u.name} className="h-full w-full object-cover" />
                              ) : (
                                <span className="text-lg font-bold text-slate-500">{u.name?.charAt(0).toUpperCase()}</span>
                              )}
                            </div>
                            <div>
                              <p className="font-bold text-slate-800 group-hover:text-blue-600 transition-colors text-lg">{u.name}</p>
                              <div className="flex items-center gap-2">
                                <p className="text-xs text-slate-500">{u.email}</p>
                                <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 bg-slate-100 text-slate-500 rounded border border-slate-200">
                                  {u.role?.replace('_', ' ')}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {u.progress_stage && (
                              <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 bg-blue-600 text-white rounded-lg shadow-sm">
                                {u.progress_stage}
                              </span>
                            )}
                            <div className="h-10 w-10 flex items-center justify-center text-slate-300 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all group-hover:text-blue-500">
                              <Eye className="h-5 w-5" />
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                    {filteredUsers.length > 10 && (
                      <div className="p-4 text-center bg-slate-50 border-t border-slate-100">
                        <button
                          onClick={() => {
                            navigate('/staff/approvals');
                            setActiveApprovalTab('all');
                          }}
                          className="text-xs font-bold text-blue-600 hover:text-blue-800 hover:underline uppercase tracking-widest flex items-center gap-2 justify-center mx-auto"
                        >
                          View all {filteredUsers.length} results in users tab
                          <ArrowRight className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* Primary Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Pending Users */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 relative group overflow-hidden">
                  <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <UserPlus className="h-16 w-16 text-amber-500" />
                  </div>
                  <div className="relative z-10">
                    <div className="h-12 w-12 bg-amber-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <UserPlus className="h-6 w-6 text-amber-600" />
                    </div>
                    <p className="text-3xl font-bold text-slate-800 mb-1">{stats.pending}</p>
                    <p className="text-sm font-medium text-slate-500">Pending Approvals</p>
                  </div>
                </div>

                {/* Approved Users */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 relative group overflow-hidden">
                  <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <CheckCircle className="h-16 w-16 text-emerald-500" />
                  </div>
                  <div className="relative z-10">
                    <div className="h-12 w-12 bg-emerald-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <CheckCircle className="h-6 w-6 text-emerald-600" />
                    </div>
                    <p className="text-3xl font-bold text-slate-800 mb-1">{stats.approved}</p>
                    <p className="text-sm font-medium text-slate-500">Active Users</p>
                  </div>
                </div>

                {/* Total Candidates */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 relative group overflow-hidden">
                  <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <Users className="h-16 w-16 text-blue-500" />
                  </div>
                  <div className="relative z-10">
                    <div className="h-12 w-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Users className="h-6 w-6 text-blue-600" />
                    </div>
                    <p className="text-3xl font-bold text-slate-800 mb-1">{stats.candidates}</p>
                    <p className="text-sm font-medium text-slate-500">Total Candidates</p>
                  </div>
                </div>

                {/* Recruiters */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 relative group overflow-hidden">
                  <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <Briefcase className="h-16 w-16 text-purple-500" />
                  </div>
                  <div className="relative z-10">
                    <div className="h-12 w-12 bg-purple-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Briefcase className="h-6 w-6 text-purple-600" />
                    </div>
                    <p className="text-3xl font-bold text-slate-800 mb-1">{stats.recruiters}</p>
                    <p className="text-sm font-medium text-slate-500">Employers</p>
                  </div>
                </div>
              </div>

              {/* Candidate Statistics Section */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-800">Candidate Pool Analytics</h2>
                    <p className="text-sm text-slate-500">Breakdown by occupation and expertise</p>
                  </div>
                  <div className="bg-emerald-50 text-emerald-700 px-3 py-1 rounded-lg text-sm font-medium border border-emerald-100">
                    Total Visible: {totalCandidates}
                  </div>
                </div>
                <div className="p-6 bg-slate-50/50">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {candidateStatistics.filter(stat => stat.occupation?.toLowerCase().includes(searchTerm.toLowerCase())).length === 0 ? (
                      <div className="col-span-full flex flex-col items-center justify-center py-12 text-slate-400 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                        <Users className="h-12 w-12 mb-3 opacity-50" />
                        <p>No analytics data available yet</p>
                      </div>
                    ) : (
                      candidateStatistics
                        .filter(stat => stat.occupation?.toLowerCase().includes(searchTerm.toLowerCase()))
                        .map((stat, index) => (
                          <div
                            key={index}
                            onClick={() => handleOccupationClick(stat.occupation)}
                            className="bg-white hover:bg-blue-50/30 p-4 rounded-xl border border-slate-200 hover:border-blue-200 shadow-sm transition-all group cursor-pointer"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="p-2 bg-slate-100 rounded-lg group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors text-slate-500">
                                <Briefcase className="h-4 w-4" />
                              </div>
                              <span className="text-2xl font-bold text-slate-800 group-hover:text-blue-700 transition-colors">{stat.count}</span>
                            </div>
                            <h3 className="text-sm font-bold text-slate-700 truncate" title={stat.occupation}>{stat.occupation}</h3>
                            <div className="w-full bg-slate-100 h-1.5 rounded-full mt-3 overflow-hidden">
                              <div className="bg-blue-500 h-full rounded-full" style={{ width: `${(stat.count / totalCandidates) * 100}%` }}></div>
                            </div>
                          </div>
                        ))
                    )}
                  </div>
                </div>
              </div>

              {/* Manual Pools Section */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-800">Manual Talent Pools</h2>
                    <p className="text-sm text-slate-500">Custom candidate groups for faster segregation</p>
                  </div>
                  <button
                    onClick={() => setShowCreatePoolModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold shadow-md shadow-blue-500/20 hover:bg-blue-700 transition-all"
                  >
                    <Plus className="h-4 w-4" />
                    Create New Pool
                  </button>
                </div>
                <div className="p-6 bg-slate-50/50">
                  {loadingPools ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : (manualPools.filter(pool =>
                    pool.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    pool.owner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    pool.source?.toLowerCase().includes(searchTerm.toLowerCase())
                  ).length === 0) ? (
                    <div className="flex flex-col items-center justify-center py-12 text-slate-400 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                      <Database className="h-12 w-12 mb-3 opacity-50" />
                      <p>{searchTerm ? `No pools found matching "${searchTerm}"` : 'No manual pools created yet'}</p>
                      <button
                        onClick={() => setShowCreatePoolModal(true)}
                        className="mt-3 text-blue-600 font-bold hover:underline"
                      >
                        Create your first pool
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {manualPools
                        .filter(pool =>
                          pool.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          pool.owner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          pool.source?.toLowerCase().includes(searchTerm.toLowerCase())
                        )
                        .map((pool) => (
                          <div
                            key={pool.id}
                            className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm transition-all group relative text-left"
                          >
                            <div className="flex justify-between items-start mb-3">
                              <div className="p-3 bg-blue-50 rounded-xl text-blue-600">
                                <Database className="h-5 w-5" />
                              </div>
                              <div className="flex gap-2">
                                {pool.is_editable && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleTogglePoolVisibility(pool, pool.is_public); }}
                                    className={`p-2 rounded-lg transition-all ${pool.is_public ? 'text-emerald-600 hover:bg-emerald-50' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
                                    title={pool.is_public ? "Public (click to make private)" : "Private (click to make public)"}
                                  >
                                    {pool.is_public ? <Globe className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                                  </button>
                                )}
                                {pool.is_editable && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleDeletePool(pool.id); }}
                                    className="p-2 text-slate-300 hover:text-red-500 rounded-lg hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100"
                                    title="Delete Pool"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col gap-1 mb-2">
                              <h3 className="text-lg font-bold text-slate-800 truncate leading-tight" title={pool.name}>{pool.name}</h3>
                              {pool.source !== 'staff' && (
                                <span className="inline-flex items-center self-start text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100">
                                  {pool.source} • {pool.owner_name}
                                </span>
                              )}
                              {pool.source === 'staff' && pool.owner_name && (
                                <span className="inline-flex items-center self-start text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                                  staff • {pool.owner_name}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-slate-500 text-sm mb-4">
                              <Users className="h-4 w-4" />
                              <span>{pool.candidate_count} candidates</span>
                            </div>
                            <button
                              onClick={() => fetchPoolCandidates(pool)}
                              className="w-full py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-bold hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center gap-2"
                            >
                              <Eye className="h-4 w-4" />
                              View Candidates
                            </button>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Candidate Pool Statistics - Also show on Recruiter Visibility tab for context */}
          {activeTab === 'recruiter-visibility' && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-8 animate-fadeIn">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-bold text-slate-800">Candidate Distribution</h2>
                  <p className="text-sm text-slate-500">Overview of available talent by role</p>
                </div>
                <span className="text-sm font-medium text-slate-600">Total: <span className="font-bold text-emerald-600">{totalCandidates}</span></span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {candidateStatistics.map((stat, index) => (
                  <div
                    key={index}
                    onClick={() => handleOccupationClick(stat.occupation)}
                    className="bg-slate-50 rounded-lg p-3 border border-slate-100 flex flex-col hover:bg-indigo-50 hover:border-indigo-200 cursor-pointer transition-colors"
                  >
                    <span className="text-xl font-bold text-slate-800">{stat.count}</span>
                    <span className="text-xs font-medium text-slate-500 truncate" title={stat.occupation}>{stat.occupation}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Approvals Tab Content */}
          {activeTab === 'approvals' && (
            <div className="space-y-6 animate-fadeIn">
              {/* Filters and Actions Bar */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 flex flex-col md:flex-row gap-4 justify-between items-center">
                <div className="relative w-full md:w-96 group">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 h-5 w-5 group-focus-within:text-blue-500 transition-colors" />
                  <input
                    type="text"
                    placeholder="Search users by name, email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium text-slate-900"
                  />
                </div>

                <div className="flex w-full md:w-auto gap-3">
                  <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-medium text-slate-700 cursor-pointer"
                  >
                    <option value="all">All Roles</option>
                    <option value="candidate">Candidate</option>
                    <option value="recruiting_partner">Employer</option>
                    <option value="sourcing_partner">Sourcing Partner</option>
                  </select>

                  <select
                    value={languageSort}
                    onChange={(e) => setLanguageSort(e.target.value)}
                    className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-medium text-slate-700 cursor-pointer"
                  >
                    <option value="none">Sort by Language</option>
                    <option value="desc">Language: High to Low</option>
                    <option value="asc">Language: Low to High</option>
                  </select>

                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg shadow-blue-500/30 transition-all hover:scale-105 active:scale-95"
                  >
                    <UserPlus className="h-5 w-5" />
                    <span>Add User</span>
                  </button>
                </div>
              </div>

              {/* Users Grid/List */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                {loading ? (
                  <div className="p-12 text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-slate-500 font-medium">Loading users...</p>
                  </div>
                ) : (
                  <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50/50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-bold">
                          <th className="px-6 py-4">User</th>
                          <th className="px-6 py-4">Role</th>
                          <th className="px-6 py-4">Status</th>
                          <th className="px-6 py-4">Progress Stage</th>
                          <th className="px-6 py-4">Profile Completion</th>
                          <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filteredUsers.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="px-6 py-12 text-center text-slate-400">
                              <div className="mx-auto h-16 w-16 bg-slate-50 rounded-full flex items-center justify-center mb-3">
                                <Search className="h-8 w-8 text-slate-300" />
                              </div>
                              <p className="font-medium">No users found matching your filters</p>
                            </td>
                          </tr>
                        ) : (
                          filteredUsers.map((u) => (
                            <tr key={u.id} className="hover:bg-blue-50/30 transition-colors group cursor-pointer" onClick={() => handleViewUser(u)}>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-4">
                                  <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200 relative">
                                    {u.photo_url ? (
                                      <img src={getPhotoUrl(u.photo_url, u.updated_at)} alt={u.name} className="h-full w-full object-cover" />
                                    ) : (
                                      <span className="text-sm font-bold text-slate-500">{u.name?.charAt(0).toUpperCase()}</span>
                                    )}
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                                  </div>
                                  <div>
                                    <p className="font-bold text-slate-800">{u.name}</p>
                                    <div className="flex items-center gap-1">
                                      <p className="text-xs text-slate-500">
                                        {u.role?.includes('partner') && currentUser?.role === 'staff' ? '(Contact Hidden)' : u.email}
                                      </p>
                                      {u.email_verified && !(u.role?.includes('partner') && currentUser?.role === 'staff') && (
                                        <BadgeCheck className="h-3 w-3 text-blue-500" aria-label="Verified Email" />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                  {getRoleIcon(u.role)}
                                  <span className="text-sm font-medium text-slate-600 capitalize">{u.role?.replace('_', ' ')}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                {getStatusBadge(u.approval_status)}
                              </td>
                              <td className="px-6 py-4">
                                {u.role === 'candidate' ? (
                                  <select
                                    value={u.progress_stage || 'A1'}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={async (e) => {
                                      e.stopPropagation();
                                      const newStage = e.target.value;
                                      let company = u.interview_company || '';
                                      if (['Interview Waiting', 'Interview Scheduled', 'Interview attended', 'Offer Received'].includes(newStage)) {
                                        const input = prompt('Enter Interview Company Name:', company);
                                        if (input === null) return; // user cancelled
                                        company = input;
                                      }
                                      try {
                                        await api.put(`/staff/users/${u.id}`, { progress_stage: newStage, interview_company: company });
                                        fetchUsers();
                                      } catch (error) {
                                        console.error('Failed to update progress stage:', error);
                                        // If showToast is available, use it, else alert
                                        alert('Failed to update progress stage');
                                      }
                                    }}
                                    className="px-2 py-1.5 text-xs font-bold rounded-lg border border-blue-200 bg-blue-50 text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 cursor-pointer w-full max-w-[150px]"
                                  >
                                    <option value="A1">A1</option>
                                    <option value="A2">A2</option>
                                    <option value="B1">B1</option>
                                    <option value="B2">B2</option>
                                    <option value="C1">C1</option>
                                    <option value="C2">C2</option>
                                    <option value="Language Learning">Language Learning</option>
                                    <option value="Document Submitted">Document Submitted</option>
                                    <option value="Screening Waiting">Screening Waiting</option>
                                    <option value="Screening Passed">Screening Passed</option>
                                    <option value="Interview Waiting">Interview Waiting</option>
                                    <option value="Interview Scheduled">Interview Scheduled</option>
                                    <option value="Interview attended">Interview attended</option>
                                    <option value="Offer Received">Offer Received</option>
                                    <option value="Visa submitted">Visa submitted</option>
                                    <option value="Visa Granted">Visa Granted</option>
                                    <option value="Migrated">Migrated</option>
                                    <option value="Rejected">Rejected</option>
                                  </select>
                                ) : (
                                  <span className="text-slate-300">—</span>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                {u.role === 'candidate' ? (
                                  (() => {
                                    const { score, missingFields } = calculateProfileCompletion(u);
                                    return (
                                      <div className="flex flex-col gap-1">
                                        <div className="flex items-center gap-2">
                                          <div className="w-16 bg-slate-200 rounded-full h-1.5 overflow-hidden">
                                            <div
                                              className={`h-full rounded-full transition-all ${score === 100 ? 'bg-emerald-500' :
                                                score > 50 ? 'bg-blue-500' : 'bg-amber-500'
                                                }`}
                                              style={{ width: `${score}%` }}
                                            />
                                          </div>
                                          <span className={`text-xs font-bold ${score === 100 ? 'text-emerald-600' :
                                            score > 50 ? 'text-blue-600' : 'text-amber-600'
                                            }`}>
                                            {score}%
                                          </span>
                                        </div>
                                        {missingFields.length > 0 && (
                                          <details className="mt-1 group">
                                            <summary className="text-[10px] font-bold text-amber-600 uppercase tracking-wider mb-1 cursor-pointer flex items-center gap-1 list-none focus:outline-none">
                                              <span>Missing Details ({missingFields.length})</span>
                                              <ChevronDown className="h-3 w-3 transition-transform group-open:rotate-180" />
                                            </summary>
                                            <div className="flex flex-wrap gap-1 mt-1 pl-2 border-l-2 border-amber-200">
                                              {missingFields.map((f, i) => (
                                                <span key={i} className="px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded text-[9px] border border-amber-100 font-medium whitespace-nowrap">
                                                  {f}
                                                </span>
                                              ))}
                                            </div>
                                          </details>
                                        )}
                                      </div>
                                    );
                                  })()
                                ) : (
                                  <span className="text-slate-300">—</span>
                                )}
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleViewUser(u); }}
                                    className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                    title="View Details"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>

                                  {u.approval_status === 'pending' && (
                                    <>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleApprove(u.id); }}
                                        className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
                                        title="Approve User"
                                      >
                                        <CheckCircle className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleReject(u.id); }}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                        title="Reject User"
                                      >
                                        <XCircle className="h-4 w-4" />
                                      </button>
                                    </>
                                  )}

                                  {u.approval_status === 'rejected' && (
                                    <>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleApprove(u.id); }}
                                        className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
                                        title="Approve User"
                                      >
                                        <CheckCircle className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleUpdateStatus(u.id, 'pending'); }}
                                        className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-all"
                                        title="Move to Pending"
                                      >
                                        <RefreshCw className="h-4 w-4" />
                                      </button>
                                    </>
                                  )}

                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedUser(u);
                                      setEditUserData({
                                        name: u.name || '',
                                        qualification: u.qualification || '',
                                        experience: u.experience || '',
                                        german_level: u.german_level || '',
                                        phone: u.phone || '',
                                        university: u.university || '',
                                        year_of_passout: u.year_of_passout || '',
                                        occupation: u.occupation || '',
                                        profile_status: u.profile_status || 'incomplete'
                                      });
                                      setShowEditModal(true);
                                    }}
                                    className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                    title="Edit User"
                                  >
                                    <Settings className="h-4 w-4" />
                                  </button>

                                  {(currentUser?.role === 'admin' || (u.role !== 'staff' && u.role !== 'admin')) && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setPasswordResetUserId(u.id);
                                        setShowPasswordModal(true);
                                      }}
                                      className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all"
                                      title="Reset Password"
                                    >
                                      <Key className="h-4 w-4" />
                                    </button>
                                  )}

                                  {u.role === 'candidate' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCandidateToAddToPool(u);
                                        setShowAddToPoolModal(true);
                                      }}
                                      className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                      title="Add to Pool"
                                    >
                                      <Database className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  {/* Pagination Controls */}
                  {filteredUsers.length > 0 && totalPages > 1 && (
                    <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex items-center justify-between">
                      <div className="text-sm text-slate-500 font-medium">
                        Showing page {currentPage} of {totalPages}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => fetchUsers(currentPage - 1)}
                          disabled={currentPage === 1}
                          className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${
                            currentPage === 1 
                              ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                              : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300'
                          }`}
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => fetchUsers(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${
                            currentPage === totalPages 
                              ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                              : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300'
                          }`}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Recruiter Visibility Tab */}
          {activeTab === 'recruiter-visibility' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 border-b border-slate-100 pb-8">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                      <Eye className="h-6 w-6 text-blue-600" />
                      Visibility Management
                    </h2>
                    <p className="text-slate-500 mt-1">Control which candidates are visible to recruiting partners</p>
                  </div>

                  <div className="w-full md:w-80">
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Select Recruiter</label>
                    <select
                      value={selectedRecruiter?.id || ''}
                      onChange={(e) => {
                        const recruiter = recruiters.find(r => r.id === parseInt(e.target.value));
                        setSelectedRecruiter(recruiter);
                        if (recruiter) {
                          fetchVisibleCandidates(recruiter.id);
                        }
                      }}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-medium text-slate-700 cursor-pointer"
                    >
                      <option value="">Choose a recruiter...</option>
                      {recruiters.map(recruiter => (
                        <option key={recruiter.id} value={recruiter.id}>{recruiter.name} {recruiter.role?.includes('partner') && currentUser?.role === 'staff' ? '(Contact Hidden)' : `(${recruiter.email})`}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {selectedRecruiter ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Available Candidates Panel */}
                    <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-bold text-slate-700 flex items-center gap-2">
                          <Users className="h-4 w-4 text-slate-400" />
                          Available Candidates
                        </h4>
                        <span className="bg-slate-200 text-slate-600 text-xs font-bold px-2 py-1 rounded-full">
                          {allCandidatesForVisibility.filter(c => !visibleCandidates.some(vc => vc.id === c.id)).length}
                        </span>
                      </div>

                      <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                        {allCandidatesForVisibility.length === 0 ? (
                          <div className="text-center py-8 text-slate-400">No candidates available</div>
                        ) : (
                          allCandidatesForVisibility
                            .filter(c => !visibleCandidates.some(vc => vc.id === c.id))
                            .map(candidate => (
                              <div key={candidate.id} className="bg-white p-3 rounded-xl border border-slate-200 hover:border-blue-300 shadow-sm transition-all group flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500 border border-slate-200">
                                    {candidate.name?.charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <p className="text-sm font-bold text-slate-800">{candidate.name}</p>
                                    <p className="text-xs text-slate-500">{candidate.occupation || 'No Role'}</p>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleGrantVisibility(selectedRecruiter.id, candidate.id)}
                                  className="p-2 bg-slate-100 text-slate-400 hover:bg-emerald-500 hover:text-white rounded-lg transition-colors"
                                  title="Grant Access"
                                >
                                  <Plus className="h-4 w-4" />
                                </button>
                              </div>
                            ))
                        )}
                      </div>
                    </div>

                    {/* Granted Candidates Panel */}
                    <div className="bg-blue-50/50 rounded-2xl p-6 border border-blue-100">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-bold text-blue-800 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-blue-500" />
                          Visible Candidates
                        </h4>
                        <span className="bg-blue-100 text-blue-600 text-xs font-bold px-2 py-1 rounded-full">
                          {visibleCandidates.length}
                        </span>
                      </div>

                      <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                        {visibleCandidates.length === 0 ? (
                          <div className="text-center py-12 border-2 border-dashed border-blue-200 rounded-xl bg-white/50">
                            <EyeOff className="h-8 w-8 text-blue-300 mx-auto mb-2" />
                            <p className="text-sm text-blue-400 font-medium">No candidates visible yet</p>
                          </div>
                        ) : (
                          visibleCandidates.map(candidate => (
                            <div key={candidate.id} className="bg-white p-3 rounded-xl border border-blue-200 shadow-sm transition-all flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-600 border border-blue-200">
                                  {candidate.name?.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <p className="text-sm font-bold text-slate-800">{candidate.name}</p>
                                  <p className="text-xs text-blue-500 font-medium">Visible</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleToggleContactUnmask(selectedRecruiter.id, candidate.id, candidate.contact_unmasked)}
                                  className={`p-2 rounded-lg transition-colors ${candidate.contact_unmasked ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' : 'bg-slate-100 text-slate-400 hover:bg-slate-200 hover:text-slate-600'}`}
                                  title={candidate.contact_unmasked ? "Hide Contact Info" : "Show Contact Info"}
                                >
                                  {candidate.contact_unmasked ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                                </button>
                                <button
                                  onClick={() => handleRevokeVisibility(selectedRecruiter.id, candidate.id)}
                                  className="p-2 bg-slate-100 text-slate-400 hover:bg-red-500 hover:text-white rounded-lg transition-colors"
                                  title="Revoke Access"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-16 bg-slate-50/50 rounded-2xl border-2 border-dashed border-slate-200">
                    <Briefcase className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-bold text-slate-700">No Recruiter Selected</h3>
                    <p className="text-slate-500 mt-1">Please select a recruiting partner from the dropdown above to manage candidate visibility.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sourcing Partners Tab */}
          {activeTab === 'sourcing-partners' && (
            <div className="space-y-6 animate-fadeIn">
              {/* Sourcing Partners List */}
              {!showPartnerCandidates && (
                <div className="space-y-6">
                  <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
                    <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                      <Users className="h-6 w-6 text-indigo-600" />
                      Sourcing Partners
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {sourcingPartners.map((partner) => (
                        <div
                          key={partner.id}
                          onClick={() => handleSelectSourcingPartner(partner)}
                          className="group bg-white rounded-2xl p-6 border border-slate-200 hover:border-indigo-400 shadow-sm hover:shadow-xl transition-all cursor-pointer relative overflow-hidden"
                        >
                          <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-50 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110" />

                          <div className="flex items-center gap-4 mb-6 relative">
                            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl shadow-lg shadow-indigo-500/20">
                              {partner.name?.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-slate-800 group-hover:text-indigo-600 transition-colors">{partner.name}</h3>
                              <p className="text-sm text-slate-500">{partner.role?.includes('partner') && currentUser?.role === 'staff' ? '(Contact Hidden)' : partner.email}</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-slate-50 rounded-xl p-3 text-center group-hover:bg-indigo-50/50 transition-colors">
                              <p className="text-2xl font-bold text-slate-800">{partner.candidate_count || 0}</p>
                              <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">Candidates</p>
                            </div>
                            <div className="bg-slate-50 rounded-xl p-3 text-center group-hover:bg-indigo-50/50 transition-colors">
                              <p className="text-2xl font-bold text-emerald-600">{partner.approved_count || 0}</p>
                              <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">Approved</p>
                            </div>
                          </div>

                          <div className="mt-6 flex items-center justify-center text-indigo-500 font-medium text-sm group-hover:translate-x-1 transition-transform">
                            View Candidates <ChevronRight className="h-4 w-4 ml-1" />
                          </div>
                        </div>
                      ))}
                    </div>

                    {sourcingPartners.length === 0 && (
                      <div className="text-center py-16 text-slate-400 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                        <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p className="font-medium">No sourcing partners found</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Interviews View */}
              {activeTab === 'interviews' && (
                <div className="space-y-6 animate-fadeIn">
                  <div className="flex justify-between items-center bg-white rounded-xl shadow-sm border border-emerald-100 p-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Interviews</h2>
                      <p className="text-sm text-gray-500 mt-1">Manage your interview schedule and joining links.</p>
                    </div>
                    <button
                      onClick={() => setShowScheduleInterviewModal(true)}
                      className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-medium rounded-xl hover:shadow-lg hover:-translate-y-0.5 transition-all flex items-center gap-2"
                    >
                      <Calendar className="w-4 h-4" />
                      Schedule New
                    </button>
                  </div>

                  <div className="space-y-4">
                    {loading ? (
                      <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
                      </div>
                    ) : interviews.length === 0 ? (
                      <div className="text-center py-12 bg-white rounded-xl border border-gray-100 shadow-sm">
                        <Calendar className="mx-auto h-12 w-12 text-blue-100 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900">No interviews scheduled</h3>
                        <p className="text-gray-500">Get started by scheduling a new interview.</p>
                      </div>
                    ) : (
                      interviews.map(interview => {
                        const statusColors = {
                          'scheduled': 'bg-amber-100 text-amber-700 border-amber-200',
                          'completed': 'bg-emerald-100 text-emerald-700 border-emerald-200',
                          'cancelled': 'bg-red-100 text-red-700 border-red-200'
                        };

                        return (
                          <div key={interview.id} className="flex flex-col sm:flex-row items-start sm:items-center bg-white rounded-2xl p-6 shadow-sm border border-gray-100 gap-6 transition-all hover:shadow-md">
                            <div className="flex flex-col items-center justify-center min-w-[100px] bg-indigo-50/50 rounded-xl p-3 border border-indigo-50/50">
                              <span className="text-2xl font-bold text-indigo-600 tracking-tight">
                                {interview.time?.substring(0, 5) || 'TBA'}
                              </span>
                              <span className="text-sm font-medium text-indigo-400 mt-1 uppercase">
                                {new Date(interview.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                              </span>
                            </div>

                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-1">
                                <h3 className="text-lg font-bold text-gray-900">{interview.candidate_name}</h3>
                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${statusColors[interview.status] || 'bg-gray-100 text-gray-700'}`}>
                                  {interview.status}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-500 font-medium">
                                <span>{interview.job_title || 'General Interview'}</span>
                              </div>
                            </div>

                            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                              <div className="text-right hidden sm:block">
                                <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Interviewer</div>
                                <div className="text-sm font-semibold text-gray-800">{interview.interviewer_name}</div>
                              </div>

                              <div className="flex items-center gap-2">
                                {interview.meeting_link ? (
                                  <a
                                    href={interview.meeting_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-bold rounded-xl transition-colors shadow-sm shadow-blue-500/20 flex items-center gap-2"
                                  >
                                    <Video className="w-4 h-4" />
                                    Join Meeting
                                  </a>
                                ) : (
                                  <span className="px-4 py-2 bg-gray-100 text-gray-500 text-sm font-medium rounded-xl">
                                    No Link
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      })
                    )}
                  </div>
                </div>
              )}

              {/* Candidates View */}
              {showPartnerCandidates && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 animate-fadeIn">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8 border-b border-slate-100 pb-8">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        {selectedSourcingPartner && (
                          <button
                            onClick={handleViewAllCandidates}
                            className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-indigo-600"
                          >
                            <ArrowLeft className="h-5 w-5" />
                          </button>
                        )}
                        <h2 className="text-2xl font-bold text-slate-800">
                          {selectedSourcingPartner ? `Candidates from ${selectedSourcingPartner.name}` : 'All Sourcing Candidates'}
                        </h2>
                      </div>
                      <p className="text-slate-500 ml-10">Manage candidates and sourcing status</p>
                    </div>

                    <select
                      value={sourcingStatusFilter}
                      onChange={(e) => {
                        setSourcingStatusFilter(e.target.value);
                        if (showPartnerCandidates && selectedSourcingPartner) {
                          fetchPartnerCandidates(selectedSourcingPartner.id);
                        } else {
                          fetchSourcingCandidates();
                        }
                      }}
                      className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-medium text-slate-700 min-w-[200px]"
                    >
                      <option value="all">All Status</option>
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="approved">Approved</option>
                      <option value="A1">A1</option>
                      <option value="A2">A2</option>
                      <option value="B1">B1</option>
                      <option value="B2">B2</option>
                      <option value="C1">C1</option>
                      <option value="C2">C2</option>
                      <option value="Language Learning">Language Learning</option>
                      <option value="Document Submitted">Document Submitted</option>
                      <option value="Screening Waiting">Screening Waiting</option>
                      <option value="Screening Passed">Screening Passed</option>
                      <option value="Interview Waiting">Interview Waiting</option>
                      <option value="Interview Scheduled">Interview Scheduled</option>
                      <option value="Interview attended">Interview attended</option>
                      <option value="Offer Received">Offer Received</option>
                      <option value="Visa submitted">Visa submitted</option>
                      <option value="Visa Granted">Visa Granted</option>
                      <option value="Migrated">Migrated</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50/50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-bold">
                          <th className="px-6 py-4">Candidate</th>
                          <th className="px-6 py-4">Role</th>
                          {!selectedSourcingPartner && <th className="px-6 py-4">Appointed By</th>}
                          <th className="px-6 py-4">Status</th>
                          <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(!sourcingCandidates || sourcingCandidates.length === 0) ? (
                          <tr>
                            <td colSpan="5" className="px-6 py-16 text-center text-slate-400">
                              <Users className="h-10 w-10 mx-auto mb-3 opacity-50" />
                              <p className="font-medium">No candidates found for this selection</p>
                            </td>
                          </tr>
                        ) : (
                          sourcingCandidates.map((candidate) => (
                            <tr key={candidate.id} className="hover:bg-indigo-50/30 transition-colors group">
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                  {candidate.is_new && (
                                    <span className="h-2 w-2 rounded-full bg-indigo-500 animate-pulse" title="New Candidate" />
                                  )}
                                  <div>
                                    <p className="font-bold text-slate-800">{candidate.name}</p>
                                    <p className="text-xs text-slate-500">{candidate.email}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <p className="text-sm font-medium text-slate-600">{candidate.occupation || 'N/A'}</p>
                              </td>
                              {!selectedSourcingPartner && (
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-2">
                                    <div className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500 border border-slate-200">
                                      {candidate.sourcing_partner_name?.charAt(0).toUpperCase()}
                                    </div>
                                    <span className="text-sm text-slate-600">{candidate.sourcing_partner_name}</span>
                                  </div>
                                </td>
                              )}
                              <td className="px-6 py-4">
                                {candidate.sourcing_status === 'published' || candidate.sourcing_status === 'draft' || candidate.sourcing_status === 'rejected' ? (
                                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${candidate.sourcing_status === 'rejected' ? 'bg-red-100 text-red-700' :
                                    candidate.sourcing_status === 'published' ? 'bg-blue-100 text-blue-700' :
                                      'bg-slate-100 text-slate-600'
                                    }`}>
                                    {candidate.sourcing_status || 'draft'}
                                  </span>
                                ) : (
                                  <select
                                    value={candidate.sourcing_status || 'approved'}
                                    onChange={async (e) => {
                                      try {
                                        await api.put(`/staff/users/${candidate.id}`, { sourcing_status: e.target.value });
                                        if (showPartnerCandidates && selectedSourcingPartner) {
                                          fetchPartnerCandidates(selectedSourcingPartner.id);
                                        } else {
                                          fetchSourcingCandidates();
                                        }
                                      } catch (error) {
                                        console.error('Failed to update stage:', error);
                                        showToast('Failed to update tracking stage');
                                      }
                                    }}
                                    className="px-2 py-1.5 text-xs font-bold rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 cursor-pointer"
                                  >
                                    <option value="approved">Approved</option>
                                    <option value="Language Learning">Language Learning</option>
                                    <option value="Document Submitted">Document Submitted</option>
                                    <option value="Screening Waiting">Screening Waiting</option>
                                    <option value="Screening Passed">Screening Passed</option>
                                    <option value="Interview Waiting">Interview Waiting</option>
                                    <option value="Interview Scheduled">Interview Scheduled</option>
                                    <option value="Interview attended">Interview attended</option>
                                    <option value="Offer Received">Offer Received</option>
                                    <option value="Visa submitted">Visa submitted</option>
                                    <option value="Visa Granted">Visa Granted</option>
                                    <option value="Migrated">Migrated</option>
                                  </select>
                                )}
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    onClick={() => handleViewUser(candidate)}
                                    className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                                    title="View Profile"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>

                                  {candidate.sourcing_status === 'published' && (
                                    <>
                                      <button
                                        onClick={() => {
                                          setCandidateToAddToPool(candidate);
                                          setShowAddToPoolModal(true);
                                        }}
                                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                        title="Add to Pool"
                                      >
                                        <Database className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={async () => {
                                          await handleApproveSourcingCandidate(candidate.id);
                                          if (showPartnerCandidates && selectedSourcingPartner) {
                                            await fetchPartnerCandidates(selectedSourcingPartner.id);
                                          } else {
                                            await fetchSourcingCandidates();
                                          }
                                          await fetchSourcingPartners();
                                        }}
                                        className="p-2 text-emerald-500 hover:bg-emerald-50 rounded-lg transition-all"
                                        title="Approve Candidate"
                                      >
                                        <CheckCircle className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={async () => {
                                          const reason = prompt('Enter rejection reason (optional):');
                                          if (reason !== null) {
                                            await handleRejectSourcingCandidate(candidate.id, reason);
                                            if (showPartnerCandidates && selectedSourcingPartner) {
                                              await fetchPartnerCandidates(selectedSourcingPartner.id);
                                            } else {
                                              await fetchSourcingCandidates();
                                            }
                                            await fetchSourcingPartners();
                                          }
                                        }}
                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                        title="Reject Candidate"
                                      >
                                        <XCircle className="h-4 w-4" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Recruiting Jobs Tab */}
          {activeTab === 'recruiting-jobs' && (
            <StaffJobsTab showToast={showToast} />
          )}

          {/* Partner Feedbacks Tab */}
          {(activeTab === 'partner-feedbacks' || activeTab === 'recruiter-feedbacks' || activeTab === 'sourcing-feedbacks') && (
            <div className="space-y-6 animate-fadeIn">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 border-b border-slate-100 pb-8">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                      <MessageSquare className="h-6 w-6 text-pink-600" />
                      Feedbacks & Requirements
                    </h2>
                    <p className="text-slate-500 mt-1">Manage inquiries and feedback from partners</p>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <select
                      value={feedbackPartnerTypeFilter}
                      onChange={(e) => {
                        setFeedbackPartnerTypeFilter(e.target.value);
                        fetchPartnerFeedbacks();
                      }}
                      className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 font-medium text-slate-700"
                    >
                      <option value="all">All Partners</option>
                      <option value="sourcing_partner">Sourcing Partners</option>
                      <option value="recruiting_partner">Employers</option>
                    </select>
                    <select
                      value={feedbackStatusFilter}
                      onChange={(e) => {
                        setFeedbackStatusFilter(e.target.value);
                        fetchPartnerFeedbacks();
                      }}
                      className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 font-medium text-slate-700"
                    >
                      <option value="all">All Status</option>
                      <option value="new">New</option>
                      <option value="read">Read</option>
                      <option value="responded">Responded</option>
                      <option value="resolved">Resolved</option>
                    </select>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50/50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-bold">
                        <th className="px-6 py-4">Partner</th>
                        <th className="px-6 py-4">Type</th>
                        <th className="px-6 py-4">Message</th>
                        <th className="px-6 py-4">Priority</th>
                        <th className="px-6 py-4">Status</th>
                        <th className="px-6 py-4">Date</th>
                        <th className="px-6 py-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {partnerFeedbacks.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-6 py-16 text-center text-slate-400">
                            <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-50" />
                            <p className="font-medium">No feedback items found</p>
                          </td>
                        </tr>
                      ) : (
                        partnerFeedbacks.map((feedback) => (
                          <tr
                            key={feedback.id}
                            onClick={() => handleViewFeedback(feedback)}
                            className={`cursor-pointer transition-colors group ${feedback.status === 'new' ? 'bg-yellow-50/50 hover:bg-yellow-50' : 'hover:bg-slate-50'
                              }`}
                          >
                            <td className="px-6 py-4">
                              <p className="font-bold text-slate-800">{feedback.partner_name}</p>
                              <p className="text-xs text-slate-500">(Contact Hidden)</p>
                            </td>
                            <td className="px-6 py-4">
                              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-600">
                                {feedback.partner_type === 'sourcing_partner' ? 'Sourcing' : 'Recruiting'}
                              </span>
                            </td>
                            <td className="px-6 py-4 group-hover:text-slate-900 text-slate-600 transition-colors">
                              <div className="truncate max-w-xs">{feedback.message || (feedback.feedback_type === 'request' ? 'Request' : 'Feedback')}</div>
                              {feedback.response_count > 0 && (
                                <div className="text-xs font-bold text-indigo-500 mt-1 flex items-center gap-1">
                                  <MessageSquare className="h-3 w-3" /> {feedback.response_count} response(s)
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${feedback.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                                feedback.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                                  'bg-blue-100 text-blue-700'
                                }`}>
                                {feedback.priority}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-bold capitalize ${feedback.status === 'new' ? 'bg-yellow-100 text-yellow-700' :
                                feedback.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' :
                                  'bg-slate-100 text-slate-600'
                                }`}>
                                {feedback.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-500 font-medium">
                              {new Date(feedback.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleViewFeedback(feedback)}
                                  className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-slate-100 rounded-lg transition-all"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                {feedback.status !== 'resolved' && (
                                  <button
                                    onClick={() => handleUpdateFeedbackStatus(feedback.id, 'resolved')}
                                    className="p-2 text-emerald-500 hover:bg-emerald-50 rounded-lg transition-all"
                                    title="Mark as Resolved"
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Stats Modal */}
          {showOccupationModal && (
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
              <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col scale-100 animate-scaleIn">
                <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0 z-10">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center">
                      <Briefcase className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-800">{selectedOccupationStats}</h2>
                      <p className="text-slate-500 text-sm">Found {occupationCandidates.length} approved candidates</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowOccupationModal(false)}
                    className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400 hover:text-red-500"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="p-0 overflow-y-auto flex-1 custom-scrollbar bg-slate-50">
                  {loading ? (
                    <div className="flex flex-col items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
                      <p className="text-slate-500">Loading candidates...</p>
                    </div>
                  ) : occupationCandidates.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                      <Users className="h-12 w-12 mb-3 opacity-30" />
                      <p>No candidates found for this occupation</p>
                    </div>
                  ) : (
                    <table className="w-full text-left border-collapse">
                      <thead className="bg-white border-b border-gray-100 sticky top-0 z-10">
                        <tr className="text-xs uppercase tracking-wider text-slate-500 font-bold">
                          <th className="px-8 py-4 bg-white">Candidate</th>
                          <th className="px-6 py-4 bg-white">Language Level</th>
                          <th className="px-6 py-4 bg-white">Experience</th>
                          <th className="px-6 py-4 bg-white text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {occupationCandidates.map((candidate) => (
                          <tr key={candidate.id} className="hover:bg-slate-50/80 transition-colors group">
                            <td className="px-8 py-4">
                              <div className="flex items-center gap-4">
                                <div className="h-10 w-10 rounded-full bg-slate-100 overflow-hidden flex-shrink-0 border border-slate-200">
                                  {candidate.photo_url ? (
                                    <img
                                      src={getPhotoUrl(candidate.photo_url, candidate.updated_at)}
                                      alt={candidate.name}
                                      className="h-full w-full object-cover"
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(candidate.name) + '&background=random';
                                      }}
                                    />
                                  ) : (
                                    <div className="h-full w-full flex items-center justify-center text-slate-400 font-bold text-sm bg-slate-100">
                                      {candidate.name?.charAt(0).toUpperCase()}
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <p className="font-bold text-slate-800">{candidate.name}</p>
                                  <p className="text-xs text-slate-500">{candidate.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 rounded-lg text-xs font-bold uppercase tracking-wider ${candidate.german_level ? 'bg-blue-50 text-blue-600 border border-blue-100' : 'bg-slate-50 text-slate-400'
                                }`}>
                                {candidate.german_level || 'Not Set'}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-slate-600 font-medium">
                                {candidate.years_experience ? `${candidate.years_experience} years` : 'Not specified'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button
                                onClick={() => {
                                  setSelectedUser(candidate);
                                  setShowUserModal(true); // Reuse existing user details modal
                                }}
                                className="px-4 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 text-xs font-bold rounded-lg transition-colors border border-blue-100"
                              >
                                View Profile
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="p-4 border-t border-gray-100 bg-white flex justify-end">
                  <button
                    onClick={() => setShowOccupationModal(false)}
                    className="px-6 py-2.5 bg-slate-100 text-slate-600 font-bold rounded-xl hover:bg-slate-200 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Feedback Modal Detail - Styled */}
          {showFeedbackModal && selectedFeedback && (
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
              <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col scale-100 animate-scaleIn">
                <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-8 py-6 flex justify-between items-center text-white">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center">
                      <MessageSquare className="h-5 w-5 text-indigo-300" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">Feedback Thread</h2>
                      <p className="text-slate-400 text-sm">{selectedFeedback.partner_name}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowFeedbackModal(false);
                      setSelectedFeedback(null);
                      setFeedbackResponses([]);
                      setResponseText('');
                    }}
                    className="p-2 hover:bg-white/10 rounded-xl transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="p-8 overflow-y-auto flex-1 bg-slate-50">
                  <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex gap-2">
                        <span className={`px-3 py-1 text-xs font-bold rounded-full ${selectedFeedback.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                          'bg-blue-100 text-blue-700'
                          }`}>
                          {selectedFeedback.priority}
                        </span>
                        <span className={`px-3 py-1 text-xs font-bold rounded-full ${selectedFeedback.status === 'resolved' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'} uppercase tracking-wider`}>
                          {selectedFeedback.status}
                        </span>
                      </div>
                      <span className="text-xs font-bold text-slate-400">
                        {new Date(selectedFeedback.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{selectedFeedback.message}</p>
                  </div>

                  <div className="space-y-4 mb-8">
                    {feedbackResponses.map((response) => (
                      <div key={response.id} className="flex gap-4">
                        <div className="h-10 w-10 rounded-full bg-indigo-100 flex-shrink-0 flex items-center justify-center font-bold text-indigo-700 border border-indigo-200">
                          {response.responder_name?.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-baseline justify-between mb-1">
                            <p className="text-sm font-bold text-slate-900">{response.responder_name}</p>
                            <span className="text-xs text-slate-400">{new Date(response.created_at).toLocaleString()}</span>
                          </div>
                          <div className="bg-white p-4 rounded-2xl rounded-tl-none shadow-sm border border-slate-200">
                            <p className="text-slate-700 whitespace-pre-wrap">{response.response_text}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {selectedFeedback.status !== 'resolved' ? (
                    <div className="sticky bottom-0 bg-white p-4 rounded-2xl shadow-lg border border-slate-100">
                      <textarea
                        value={responseText}
                        onChange={(e) => setResponseText(e.target.value)}
                        placeholder="Type a response..."
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 resize-none min-h-[100px] mb-4 text-slate-900"
                      />
                      <div className="flex justify-end gap-3">
                        <button
                          onClick={() => setShowFeedbackModal(false)}
                          className="px-6 py-2.5 font-bold text-slate-500 hover:bg-slate-50 rounded-xl transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleRespondToFeedback}
                          className="px-6 py-2.5 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 shadow-lg shadow-indigo-500/30 transition-all flex items-center gap-2"
                        >
                          <Send className="h-4 w-4" />
                          Send Response
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-green-50 border border-green-100 rounded-2xl p-6 text-center shadow-sm">
                      <p className="text-green-800 font-bold flex items-center justify-center gap-2">
                        <CheckCircle className="h-5 w-5" />
                        This feedback has been marked as resolved
                      </p>
                      <button
                        onClick={() => setShowFeedbackModal(false)}
                        className="mt-4 px-6 py-2 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 transition-all shadow-md"
                      >
                        Close Thread
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
                <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-8 border-b border-slate-100 pb-8">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                      <Bell className="h-6 w-6 text-amber-500" />
                      Notifications
                    </h2>
                    <p className="text-slate-500 mt-1">Stay updated with system activities</p>
                  </div>

                  <div className="flex items-center gap-3">
                    <select
                      value={notificationDateSort}
                      onChange={(e) => setNotificationDateSort(e.target.value)}
                      className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 font-medium text-slate-700"
                    >
                      <option value="desc">Newest First</option>
                      <option value="asc">Oldest First</option>
                    </select>
                    <select
                      value={notificationStatusFilter}
                      onChange={(e) => setNotificationStatusFilter(e.target.value)}
                      className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 font-medium text-slate-700"
                    >
                      <option value="all">All</option>
                      <option value="unread">Unread</option>
                      <option value="read">Read</option>
                    </select>
                  </div>
                </div>

                {notifications.length === 0 ? (
                  <div className="text-center py-16 text-slate-400 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                    <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="font-medium">No notifications found</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {([...notifications]
                      .filter(notification => {
                        if (notificationStatusFilter === 'unread') return !notification.is_read;
                        if (notificationStatusFilter === 'read') return notification.is_read;
                        return true;
                      })
                      .sort((a, b) => {
                        const da = new Date(a.created_at).getTime();
                        const db = new Date(b.created_at).getTime();
                        return notificationDateSort === 'asc' ? da - db : db - da;
                      }))
                      .map(notification => (
                        <div
                          key={notification.id}
                          className={`group p-5 rounded-2xl border transition-all ${!notification.is_read
                            ? 'bg-amber-50/50 border-amber-200 shadow-sm'
                            : 'bg-white border-slate-100'
                            }`}
                        >
                          <div className="flex items-start gap-4">
                            <div className={`p-3 rounded-xl flex-shrink-0 ${!notification.is_read ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-400'
                              }`}>
                              {notification.type === 'interest' ? <UserCheck className="h-6 w-6" /> :
                                notification.type === 'resume_download' ? <Download className="h-6 w-6" /> :
                                  <Bell className="h-6 w-6" />}
                            </div>

                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                {!notification.is_read && (
                                  <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                                )}
                                <p className={`text-slate-800 ${!notification.is_read ? 'font-bold' : 'font-medium'}`}>
                                  {notification.message}
                                </p>
                              </div>
                              <p className="text-xs text-slate-400 font-medium">
                                {new Date(notification.created_at).toLocaleString()}
                              </p>

                              {notification.type === 'interest' && notification.reference_id && (
                                <div className="mt-4 flex gap-3">
                                  <button
                                    onClick={() => handleApproveInterest(notification)}
                                    className="px-4 py-2 bg-indigo-600 text-white text-xs font-bold rounded-lg hover:bg-indigo-700 transition-colors"
                                  >
                                    Approve Access
                                  </button>
                                </div>
                              )}
                            </div>

                            {!notification.is_read && (
                              <button
                                onClick={() => handleMarkAsRead(notification.id)}
                                className="p-2 text-slate-400 hover:text-amber-500 hover:bg-amber-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                                title="Mark as Read"
                              >
                                <Check className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Recruiter Requests Tab */}
          {activeTab === 'recruiter-requests' && !showRequestCandidates && (
            <div className="space-y-6 animate-fadeIn">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
                <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-8 border-b border-slate-100 pb-8">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                      <FileText className="h-6 w-6 text-indigo-600" />
                      Recruiter Requests
                    </h2>
                    <p className="text-slate-500 mt-1">Manage and fulfill candidate requests from recruiters</p>
                  </div>
                </div>

                {recruiterRequests.length === 0 ? (
                  <div className="text-center py-16 text-slate-400 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="font-medium">{searchTerm ? `No requests found matching "${searchTerm}"` : 'No active requests found'}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-6">
                    {recruiterRequests
                      .filter(request =>
                        !searchTerm ||
                        request.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        request.requirements?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        request.recruiter_name?.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map(request => (
                        <div key={request.id} className="group bg-white rounded-2xl p-6 border border-slate-200 hover:border-indigo-300 hover:shadow-lg transition-all relative overflow-hidden">
                          <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50 rounded-bl-full -mr-10 -mt-10 transition-transform group-hover:scale-110" />

                          <div className="flex flex-col md:flex-row justify-between items-start gap-6 relative z-10">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${request.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                                  request.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                    'bg-amber-100 text-amber-700'
                                  }`}>
                                  {request.status || 'pending'}
                                </span>
                                <span className="text-xs font-bold text-slate-400 flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {new Date(request.created_at).toLocaleDateString()}
                                </span>
                              </div>

                              <h3 className="text-lg font-bold text-slate-800 mb-2">
                                {request.title || request.requirements || `Request #${request.id}`}
                              </h3>
                              {request.requirements && (
                                <p className="text-sm text-slate-600 mb-2 break-words">
                                  {request.requirements}
                                </p>
                              )}

                              <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                                <div className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center font-bold text-xs">
                                  {request.recruiter_name?.charAt(0).toUpperCase()}
                                </div>
                                <span className="font-medium">{request.recruiter_name}</span>
                                <span className="text-slate-300">•</span>
                                <span>Hidden for privacy</span>
                              </div>

                              {request.granted_candidates && request.granted_candidates.length > 0 && (
                                <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg w-fit text-sm font-medium border border-emerald-100">
                                  <CheckCircle className="h-4 w-4" />
                                  {request.granted_candidates.length} candidate(s) granted
                                </div>
                              )}
                            </div>

                            <div className="flex gap-2 items-center flex-wrap md:flex-nowrap">
                              {request.status === 'pending' && (
                                <button
                                  onClick={() => {
                                    setRequestToReject(request);
                                    setShowRejectRequestModal(true);
                                  }}
                                  className="px-5 py-2.5 bg-white border border-red-200 text-red-600 rounded-xl hover:bg-red-50 text-sm font-medium transition-all shadow-sm flex items-center gap-2"
                                >
                                  Decline Request
                                </button>
                              )}
                              {request.admin_comment && request.status !== 'pending' && (
                                <div className="absolute top-4 right-4 bg-slate-50 text-slate-600 text-xs px-2 py-1 rounded border border-slate-200 z-10 hidden group-hover:block" title="Admin Comment">
                                  {request.admin_comment}
                                </div>
                              )}
                              <button
                                onClick={() => handleManageCandidates(request)}
                                className="px-6 py-2.5 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 shadow-md shadow-indigo-500/20 transition-all flex items-center gap-2"
                              >
                                <Users className="h-4 w-4" />
                                Manage Candidates
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Manage Recruiter Interests Tab */}
          {activeTab === 'recruiter-interests' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
                <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-8 border-b border-slate-100 pb-8">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                      <Heart className="h-6 w-6 text-pink-500" />
                      Recruiter Interests
                    </h2>
                    <p className="text-slate-500 mt-1">Track and manage recruiter interests in candidates</p>
                  </div>

                  <div className="flex gap-3">
                    <select
                      value={interestStatusFilter}
                      onChange={(e) => {
                        const newStatus = e.target.value;
                        setInterestStatusFilter(newStatus);
                        fetchRecruiterInterests(newStatus, interestTypeFilter);
                      }}
                      className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 font-medium text-slate-700"
                    >
                      <option value="all">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="acked">Acked</option>
                      <option value="closed">Closed</option>
                    </select>
                    <select
                      value={interestTypeFilter}
                      onChange={(e) => {
                        const newType = e.target.value;
                        setInterestTypeFilter(newType);
                        fetchRecruiterInterests(interestStatusFilter, newType);
                      }}
                      className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 font-medium text-slate-700"
                    >
                      <option value="all">All Types</option>
                      <option value="interest">Individual</option>
                      <option value="pool_interest">Pool Interest</option>
                    </select>
                  </div>
                </div>

                {recruiterInterests.filter(i =>
                  !searchTerm ||
                  i.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  i.recruiter_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  i.pool_name?.toLowerCase().includes(searchTerm.toLowerCase())
                ).length === 0 ? (
                  <div className="text-center py-16 text-slate-400 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                    <Heart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="font-medium">{searchTerm ? `No interests found matching "${searchTerm}"` : 'No recruiter interests found'}</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {recruiterInterests
                      .filter(i =>
                        !searchTerm ||
                        i.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        i.recruiter_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        i.pool_name?.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map((interest) => (
                        <div
                          key={interest.id}
                          className={`rounded-2xl p-6 border-2 transition-all ${interest.status === 'closed' ? 'bg-slate-50 border-slate-200 opacity-75' :
                            interest.status === 'acked' ? 'bg-emerald-50/50 border-emerald-200' :
                              'bg-white border-slate-200 hover:border-pink-300 shadow-sm hover:shadow-md'
                            }`}
                        >
                          <div className="flex flex-col md:flex-row justify-between items-start gap-6">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-4">
                                <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${interest.type === 'pool_interest' ? 'bg-purple-100 text-purple-700' : 'bg-pink-100 text-pink-700'
                                  }`}>
                                  {interest.type === 'pool_interest' ? 'Pool Interest' : 'Individual Interest'}
                                </span>
                                <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${interest.status === 'closed' ? 'bg-slate-200 text-slate-600' :
                                  interest.status === 'acked' ? 'bg-emerald-100 text-emerald-700' :
                                    'bg-amber-100 text-amber-700'
                                  }`}>
                                  {interest.status === 'acked' ? 'Acked' : interest.status}
                                </span>
                                <span className="text-xs font-bold text-slate-400">
                                  {new Date(interest.created_at).toLocaleString()}
                                </span>
                              </div>

                              <div className="flex items-center gap-2 mb-2">
                                <p className="text-sm text-slate-500 font-medium">From:</p>
                                <span className="font-bold text-slate-800">{interest.recruiter_name || 'Unknown'}</span>
                                <span className="text-slate-400 text-sm">(Hidden for privacy)</span>
                              </div>

                              {interest.pool_name && (
                                <div className="flex items-center gap-2 mb-2">
                                  <Database className="h-4 w-4 text-slate-400" />
                                  <span className="font-bold text-slate-700">{interest.pool_name}</span>
                                </div>
                              )}

                              <div className="bg-white/50 p-4 rounded-xl border border-slate-100 mt-3 mb-4">
                                <p className="text-slate-600 italic">"{interest.message}"</p>
                              </div>

                              {(interest.acked_by_name || interest.closed_by_name) && (
                                <div className="flex gap-4 text-xs font-medium text-slate-500 border-t border-slate-100 pt-3">
                                  {interest.acked_by_name && (
                                    <span className="flex items-center gap-1.5 text-emerald-600">
                                      <CheckCircle className="h-3.5 w-3.5" />
                                      Acked by {interest.acked_by_name}
                                    </span>
                                  )}
                                  {interest.closed_by_name && (
                                    <span className="flex items-center gap-1.5 text-slate-500">
                                      <Lock className="h-3.5 w-3.5" />
                                      Closed by {interest.closed_by_name}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>

                            <div className="flex flex-col gap-2 min-w-[140px]">
                              {interest.status === 'pending' && (
                                <button
                                  onClick={() => handleAckInterest(interest.id)}
                                  className="px-4 py-2 bg-emerald-500 text-white text-sm font-bold rounded-xl hover:bg-emerald-600 shadow-lg shadow-emerald-500/20 transition-all"
                                >
                                  Acknowledge
                                </button>
                              )}
                              {interest.status === 'acked' && (
                                <button
                                  onClick={() => handleMarkInterestClosed(interest.id)}
                                  className="px-4 py-2 bg-slate-800 text-white text-sm font-bold rounded-xl hover:bg-slate-900 shadow-lg shadow-slate-500/20 transition-all"
                                >
                                  Close Ticket
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Candidates / Candidate Detail */}
                          {(interest.candidates?.length > 0 || interest.candidate) && (
                            <div className="mt-6 pt-6 border-t border-slate-100">
                              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">
                                {interest.candidates ? `Candidates (${interest.candidates.length})` : 'Target Candidate'}
                              </p>

                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {(interest.candidates || [interest.candidate]).map((candidate) => (
                                  <div key={candidate.id} className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl">
                                    {candidate.photo_url ? (
                                      <img src={getPhotoUrl(candidate.photo_url, candidate.updated_at)} alt={candidate.name} className="h-10 w-10 rounded-full object-cover" />
                                    ) : (
                                      <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-500">
                                        {candidate.name?.charAt(0).toUpperCase()}
                                      </div>
                                    )}
                                    <div>
                                      <p className="font-bold text-sm text-slate-800 truncate">{candidate.name}</p>
                                      <p className="text-xs text-slate-500 truncate">{candidate.occupation || 'No occupation'}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Request Candidates View */}
          {activeTab === 'recruiter-requests' && showRequestCandidates && selectedRequest && (
            <div className="space-y-6 animate-fadeIn">
              {/* Header and Back Button */}
              <div className="flex items-center justify-between">
                <button
                  onClick={() => {
                    setShowRequestCandidates(false);
                    setSelectedRequest(null);
                    setSelectedCandidatesCart([]);
                    setApprovedCandidatesForRequest([]);
                  }}
                  className="group flex items-center gap-2 text-slate-500 hover:text-indigo-600 font-bold transition-colors"
                >
                  <div className="p-2 bg-white rounded-full border border-slate-200 group-hover:border-indigo-200 transition-colors">
                    <ArrowLeft className="h-4 w-4" />
                  </div>
                  Back to Requests
                </button>

                {selectedCandidatesCart.length > 0 && (
                  <div className="flex items-center gap-4 bg-indigo-600 text-white px-4 py-2 rounded-full shadow-lg shadow-indigo-500/30 animate-scaleIn">
                    <span className="font-bold text-sm">{selectedCandidatesCart.length} selected</span>
                    <div className="h-4 w-px bg-white/20" />
                    <button
                      onClick={() => setSelectedCandidatesCart([])}
                      className="text-xs font-medium hover:text-indigo-200"
                    >
                      Clear
                    </button>
                    <button
                      onClick={() => handleApproveCandidateForRequest(selectedCandidatesCart)}
                      className="bg-white text-indigo-600 px-3 py-1 rounded-full text-xs font-bold hover:bg-indigo-50 transition-colors"
                    >
                      Approve
                    </button>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-slate-800 mb-2">
                    {selectedRequest.title || selectedRequest.requirements || `Request #${selectedRequest.id}`}
                  </h2>
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <User className="h-4 w-4" />
                    <span className="font-bold">{selectedRequest.recruiter_name}</span>
                    <span className="text-slate-300">•</span>
                    <span>Hidden for privacy</span>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row gap-6 mb-8">
                  {/* Search */}
                  <div className="flex-1 relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search candidates..."
                      value={candidateSearchTerm}
                      onChange={(e) => setCandidateSearchTerm(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-slate-900"
                    />
                  </div>

                  {/* Quick Filters */}
                  <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 custom-scrollbar">
                    <button
                      onClick={() => setSelectedOccupationFilter('all')}
                      className={`px-4 py-2 rounded-xl font-bold text-sm whitespace-nowrap transition-all ${selectedOccupationFilter === 'all'
                        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/30'
                        : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                        }`}
                    >
                      All ({allCandidatesForVisibility.length})
                    </button>
                    {getOccupationCategories().map(occupation => {
                      const count = allCandidatesForVisibility.filter(c =>
                        occupation === 'Not Specified' ? !c.occupation : c.occupation === occupation
                      ).length;
                      if (count === 0) return null;
                      return (
                        <button
                          key={occupation}
                          onClick={() => setSelectedOccupationFilter(occupation)}
                          className={`px-4 py-2 rounded-xl font-bold text-sm whitespace-nowrap transition-all ${selectedOccupationFilter === occupation
                            ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/30'
                            : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                            }`}
                        >
                          {occupation} ({count})
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[600px] overflow-y-auto custom-scrollbar pr-2">
                  {getFilteredCandidates().length === 0 ? (
                    <div className="col-span-full py-16 text-center text-slate-400 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                      <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p className="font-medium">No candidates found</p>
                      <button
                        onClick={() => { setCandidateSearchTerm(''); setSelectedOccupationFilter('all'); }}
                        className="text-indigo-600 font-bold mt-2 hover:underline"
                      >
                        Clear Filters
                      </button>
                    </div>
                  ) : (
                    getFilteredCandidates().map(candidate => {
                      const isInCart = selectedCandidatesCart.includes(candidate.id);
                      const isAlreadyApproved = approvedCandidatesForRequest.some(ac => ac.id === candidate.id);

                      return (
                        <div
                          key={candidate.id}
                          onClick={() => !isAlreadyApproved && toggleCandidateInCart(candidate.id)}
                          className={`group p-4 rounded-xl border-2 transition-all cursor-pointer relative overflow-hidden ${isAlreadyApproved ? 'bg-emerald-50/50 border-emerald-100 opacity-60' :
                            isInCart ? 'bg-indigo-50 border-indigo-500 shadow-md' :
                              'bg-white border-slate-100 hover:border-indigo-300 hover:shadow-lg'
                            }`}
                        >
                          <div className="flex items-start gap-4">
                            <div className="relative">
                              {candidate.photo_url ? (
                                <img src={getPhotoUrl(candidate.photo_url, candidate.updated_at)} alt={candidate.name} className="h-12 w-12 rounded-full object-cover border-2 border-white shadow-sm" />
                              ) : (
                                <div className={`h-12 w-12 rounded-full flex items-center justify-center font-bold text-lg border-2 border-white shadow-sm ${isInCart ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500'
                                  }`}>
                                  {candidate.name?.charAt(0).toUpperCase()}
                                </div>
                              )}
                              {isInCart && (
                                <div className="absolute -bottom-1 -right-1 bg-indigo-600 text-white rounded-full p-1 border-2 border-white">
                                  <Check className="h-3 w-3" />
                                </div>
                              )}
                              {isAlreadyApproved && (
                                <div className="absolute -bottom-1 -right-1 bg-emerald-500 text-white rounded-full p-1 border-2 border-white">
                                  <Check className="h-3 w-3" />
                                </div>
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <p className={`font-bold truncate ${isInCart ? 'text-indigo-900' : 'text-slate-800'}`}>
                                {candidate.name}
                              </p>
                              <p className="text-xs text-slate-500 truncate mb-2">{candidate.email}</p>

                              <div className="flex flex-wrap gap-1">
                                {candidate.occupation && (
                                  <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-md font-medium">
                                    {candidate.occupation}
                                  </span>
                                )}
                                {candidate.german_level && (
                                  <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-md font-medium">
                                    {candidate.german_level}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {approvedCandidatesForRequest.length > 0 && (
                  <div className="mt-8 pt-8 border-t border-slate-100">
                    <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-emerald-500" />
                      Approved for this Request ({approvedCandidatesForRequest.length})
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {approvedCandidatesForRequest.map(candidate => (
                        <div key={candidate.id} className="flex items-center gap-2 pl-2 pr-3 py-1.5 bg-emerald-50 text-emerald-800 rounded-full border border-emerald-100">
                          <div className="h-6 w-6 rounded-full bg-white flex items-center justify-center text-xs font-bold text-emerald-600">
                            {candidate.name?.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm font-bold">{candidate.name}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveCandidateFromRequest(candidate.id);
                            }}
                            className="p-1 hover:bg-emerald-200 rounded-full transition-colors ml-1"
                            title="Remove Access"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div >

      {/* User Details Modal - Unified View & Edit */}
      {
        showUserModal && selectedUser && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-scaleIn border border-slate-200">
              <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-white">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">Candidate Profile</h2>
                  <p className="text-slate-500 text-sm mt-1">Review and manage profile for <span className="font-bold text-indigo-600">{selectedUser.name}</span></p>
                </div>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="flex-1 overflow-hidden">
                <CandidateProfileView
                  candidate={selectedUser}
                  embedded={true}
                  onClose={() => setShowUserModal(false)}
                  onSaved={() => {
                    handleEditUser(null, false);
                    // Keep modal open to show changes
                  }}
                  onAction={(action, data) => {
                    if (action === 'refresh') handleEditUser(null, false);
                  }}
                />
              </div>
            </div>
          </div>
        )
      }

      {/* Edit User Modal - Comprehensive Profile Editor */}
      {
        showEditModal && selectedUser && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-xl max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col animate-scaleIn border border-slate-200">
              <div className="bg-white px-8 py-5 flex justify-between items-center border-b border-slate-100">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800 mb-1">Edit User Profile</h2>
                  <p className="text-slate-500 text-sm">Edit all profile details for <span className="font-bold text-indigo-600">{selectedUser.name}</span></p>
                </div>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditUserData({});
                  }}
                  className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="p-8 overflow-y-auto flex-1">
                <ProfileEditor
                  user={selectedUser}
                  onClose={() => {
                    setShowEditModal(false);
                    setEditUserData({});
                  }}
                  onSaved={handleEditUser}
                  role="staff"
                />
              </div>
            </div>
          </div>
        )
      }

      {/* Create User Modal */}
      {
        showCreateModal && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-8 max-h-[90vh] overflow-y-auto animate-scaleIn border border-slate-200">
              <div className="flex justify-between items-center mb-8 border-b border-slate-100 pb-4">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">Create New User</h2>
                  <p className="text-slate-500 text-sm mt-1">Add a new user to the platform</p>
                </div>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateUserForm({
                      name: '',
                      email: '',
                      password: '',
                      role: 'candidate',
                      approval_status: 'approved',
                      profile_status: 'incomplete'
                    });
                  }}
                  className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <form onSubmit={handleCreateUser} className="space-y-5">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Full Name *</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <input
                      type="text"
                      value={createUserForm.name}
                      onChange={(e) => setCreateUserForm({ ...createUserForm, name: e.target.value })}
                      className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-medium text-slate-900"
                      placeholder="John Doe"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Email Address *</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <input
                      type="email"
                      value={createUserForm.email}
                      onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
                      className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-medium text-slate-900"
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Password *</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <input
                      type="password"
                      value={createUserForm.password}
                      onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
                      className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-medium text-slate-900"
                      placeholder="••••••••"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">Role *</label>
                    <div className="relative">
                      <input
                        type="text"
                        readOnly
                        value={createUserForm.role === 'candidate' ? 'Student' :
                          createUserForm.role === 'staff' ? 'Staff' :
                            createUserForm.role === 'recruiting_partner' ? 'Recruiter' :
                              createUserForm.role === 'sourcing_partner' ? 'Source' : createUserForm.role}
                        className="hidden" // Hidden input for logic if needed, but we use select
                      />
                      <select
                        value={createUserForm.role}
                        onChange={(e) => setCreateUserForm({ ...createUserForm, role: e.target.value })}
                        className="w-full pl-4 pr-10 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-medium text-slate-900 appearance-none"
                        required
                      >
                        <option value="candidate">Candidate</option>
                        <option value="recruiting_partner">Employer</option>
                        <option value="sourcing_partner">Sourcing Partner</option>
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">Status</label>
                    <div className="relative">
                      <select
                        value={createUserForm.approval_status}
                        onChange={(e) => setCreateUserForm({ ...createUserForm, approval_status: e.target.value })}
                        className="w-full pl-4 pr-10 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-medium text-slate-900 appearance-none"
                      >
                        <option value="approved">Approved</option>
                        <option value="pending">Pending</option>
                        <option value="rejected">Rejected</option>
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-4 mt-8 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setCreateUserForm({
                        name: '',
                        email: '',
                        password: '',
                        role: 'candidate',
                        approval_status: 'approved',
                        profile_status: 'incomplete'
                      });
                    }}
                    className="flex-1 px-4 py-3 bg-white border-2 border-slate-200 text-slate-700 font-bold rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 shadow-lg shadow-indigo-500/30 transition-all"
                  >
                    Create User
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Password Reset Modal */}
      {
        showPasswordModal && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 animate-scaleIn border border-slate-200">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h2 className="text-xl font-bold text-slate-800">
                    {passwordResetUserId ? 'Reset User Password' : 'Reset Password'}
                  </h2>
                  <p className="text-slate-500 text-sm mt-1">Enter a new secure password</p>
                </div>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setNewPassword('');
                    setConfirmPassword('');
                    setPasswordError('');
                    setPasswordResetUserId(null);
                  }}
                  className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <form onSubmit={handlePasswordReset} className="space-y-5">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-medium text-slate-900"
                      placeholder="Enter new password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-indigo-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-medium text-slate-900"
                      placeholder="Confirm new password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-indigo-600 transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                {passwordError && (
                  <div className="p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3 text-red-700 text-sm font-medium animate-pulse">
                    <AlertCircle className="h-5 w-5 flex-shrink-0" />
                    {passwordError}
                  </div>
                )}

                <div className="flex gap-4 pt-4 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordModal(false);
                      setNewPassword('');
                      setConfirmPassword('');
                      setPasswordError('');
                      setPasswordResetUserId(null);
                    }}
                    className="flex-1 px-4 py-3 bg-white border-2 border-slate-200 text-slate-700 font-bold rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 shadow-lg shadow-indigo-500/30 transition-all"
                  >
                    Reset Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }


      {/* Create Pool Modal */}
      {
        showCreatePoolModal && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 animate-scaleIn border border-slate-200">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-slate-800 text-left">Create New Talent Pool</h2>
                <button onClick={() => setShowCreatePoolModal(false)} className="p-2 hover:bg-slate-100 rounded-full text-slate-400">
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2 text-left">Pool Name</label>
                  <input
                    type="text"
                    value={newPoolName}
                    onChange={(e) => setNewPoolName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none font-medium text-slate-900 text-left"
                    placeholder="e.g., German Speaking Nurses"
                  />
                </div>
                <div className="flex items-center gap-3 mt-2 mb-4 p-4 bg-slate-50 border border-slate-100 rounded-xl">
                  <input
                    type="checkbox"
                    id="poolPublicToggle"
                    checked={newPoolIsPublic}
                    onChange={(e) => setNewPoolIsPublic(e.target.checked)}
                    className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500/20"
                  />
                  <label htmlFor="poolPublicToggle" className="flex flex-col text-left cursor-pointer">
                    <span className="text-sm font-bold text-slate-800">Make Pool Public</span>
                    <span className="text-xs text-slate-500">Other staff members will be able to see and use this pool</span>
                  </label>
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowCreatePoolModal(false)}
                    className="flex-1 py-3 border-2 border-slate-200 rounded-xl font-bold text-slate-600 hover:bg-slate-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreatePool}
                    className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold shadow-lg shadow-blue-500/30 hover:bg-blue-700 transition-all"
                  >
                    Create Pool
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Pool Candidates Modal */}
      {
        showPoolCandidatesModal && selectedPool && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fadeIn">
            <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col animate-scaleIn border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                <div className="text-left">
                  <h2 className="text-2xl font-bold text-slate-800">{selectedPool.name}</h2>
                  <p className="text-sm text-slate-500">Candidates in this pool</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowSelectCandidateModal(true)}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold flex items-center gap-2 hover:bg-blue-700 transition-colors shadow-sm"
                  >
                    <Plus className="h-4 w-4" />
                    Add Candidates
                  </button>
                  <button onClick={() => setShowPoolCandidatesModal(false)} className="p-2 hover:bg-slate-200 rounded-full text-slate-400 transition-colors">
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 custom-scrollbar text-left">
                {poolCandidates.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                    <Users className="h-16 w-16 mb-4 opacity-20" />
                    <p className="text-lg font-medium">No candidates in this pool yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {poolCandidates.map(candidate => (
                      <div key={candidate.id} className="flex items-center gap-4 p-4 rounded-2xl border border-slate-100 bg-white hover:border-blue-200 hover:shadow-md transition-all group">
                        <div className="h-14 w-14 rounded-xl bg-slate-100 overflow-hidden flex-shrink-0 border border-slate-200">
                          <img
                            src={getPhotoUrl(candidate.photo_url, candidate.updated_at) || `https://ui-avatars.com/api/?name=${encodeURIComponent(candidate.name)}&background=random`}
                            alt={candidate.name}
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-slate-800 truncate">{candidate.name}</h3>
                          <p className="text-xs text-slate-500 truncate">{candidate.occupation || 'Candidate'}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${candidate.german_level ? 'bg-blue-50 text-blue-600' : 'bg-slate-50 text-slate-400'
                              }`}>
                              {candidate.german_level || 'No German Level'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => {
                              setShowPoolCandidatesModal(false);
                              handleViewUser(candidate);
                              markCandidateAsViewed(candidate);
                            }}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="View Profile"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleRemoveFromPool(candidate.id)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                            title="Remove from Pool"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      }

      {/* Select Candidate For Pool Modal (Reverse Flow) */}
      {
        showSelectCandidateModal && selectedPool && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col animate-scaleIn border border-slate-200">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Add Candidates to Pool</h2>
                  <p className="text-sm text-slate-500">Adding to: {selectedPool.name}</p>
                </div>
                <button onClick={() => setShowSelectCandidateModal(false)} className="p-2 hover:bg-slate-200 rounded-full text-slate-400 transition-colors">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="p-4 border-b border-slate-100 bg-white sticky top-0 z-10">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search candidates by name or email..."
                    value={candidateSearchQuery}
                    onChange={(e) => setCandidateSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none font-medium text-slate-900"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                <div className="space-y-2">
                  {users.filter(u =>
                    u.role === 'candidate' &&
                    u.approval_status === 'approved' &&
                    !poolCandidates.some(pc => pc.id === u.id) &&
                    (u.name?.toLowerCase().includes(candidateSearchQuery.toLowerCase()) ||
                      u.email?.toLowerCase().includes(candidateSearchQuery.toLowerCase()))
                  ).length === 0 ? (
                    <p className="text-center py-8 text-slate-500 italic">No available candidates found matching your search.</p>
                  ) : (
                    users.filter(u =>
                      u.role === 'candidate' &&
                      u.approval_status === 'approved' &&
                      !poolCandidates.some(pc => pc.id === u.id) &&
                      (u.name?.toLowerCase().includes(candidateSearchQuery.toLowerCase()) ||
                        u.email?.toLowerCase().includes(candidateSearchQuery.toLowerCase()))
                    ).map(candidate => (
                      <div key={candidate.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-100 hover:border-blue-200 hover:bg-blue-50/30 transition-all group">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold overflow-hidden border border-slate-200">
                            {candidate.photo_url ? (
                              <img src={getPhotoUrl(candidate.photo_url, candidate.updated_at)} alt={candidate.name} className="h-full w-full object-cover" />
                            ) : (
                              candidate.name.charAt(0).toUpperCase()
                            )}
                          </div>
                          <div>
                            <h4 className="font-bold text-slate-800 text-sm">{candidate.name}</h4>
                            <p className="text-xs text-slate-500">{candidate.email}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddCandidateToPool(candidate)}
                          className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 rounded-lg text-xs font-bold hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all shadow-sm"
                        >
                          Add
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Add To Pool Modal */}
      {
        showAddToPoolModal && candidateToAddToPool && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 animate-scaleIn border border-slate-200">
              <div className="flex justify-between items-center mb-6">
                <div className="text-left">
                  <h2 className="text-xl font-bold text-slate-800">Add to Pool</h2>
                  <p className="text-sm text-slate-500">Select a pool for {candidateToAddToPool.name}</p>
                </div>
                <button onClick={() => { setShowAddToPoolModal(false); setCandidateToAddToPool(null); }} className="p-2 hover:bg-slate-100 rounded-full text-slate-400">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-3 max-h-64 overflow-y-auto pr-2 custom-scrollbar text-left">
                {manualPools.filter(p => p.is_editable).length === 0 ? (
                  <p className="text-slate-500 italic py-4">No staff pools available to add candidates to. Please create a pool in the Overview tab first.</p>
                ) : (
                  manualPools.filter(p => p.is_editable).map(pool => (
                    <button
                      key={pool.id}
                      onClick={() => handleAddToPool(pool.id)}
                      className="w-full p-4 flex items-center justify-between rounded-xl border border-slate-100 hover:border-blue-300 hover:bg-blue-50/50 transition-all font-bold text-slate-700"
                    >
                      <div className="flex items-center gap-3">
                        <Database className="h-5 w-5 text-blue-500" />
                        <span>{pool.name}</span>
                      </div>
                      <span className="text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded-lg">
                        {pool.candidate_count}
                      </span>
                    </button>
                  ))
                )}
              </div>

              <div className="mt-8 pt-6 border-t border-slate-100">
                <button
                  onClick={() => { setShowAddToPoolModal(false); setCandidateToAddToPool(null); }}
                  className="w-full py-3 border-2 border-slate-200 rounded-xl font-bold text-slate-600 hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Reject Request Modal */}
      {
        showRejectRequestModal && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6">
              <h3 className="text-xl font-bold text-slate-800 mb-2">Decline Request</h3>
              <p className="text-sm text-slate-500 mb-6">
                Please provide a reason for declining this request. This will be visible to the recruiter.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Reason for Decline</label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    className="w-full h-32 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500 transition-all text-sm resize-none text-slate-900"
                    placeholder="e.g. Candidates not available for these requirements..."
                    disabled={rejectingRequest}
                  />
                </div>
              </div>
              <div className="mt-8 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowRejectRequestModal(false);
                    setRequestToReject(null);
                    setRejectReason('');
                  }}
                  disabled={rejectingRequest}
                  className="px-4 py-2 bg-white text-slate-700 font-medium text-sm rounded-xl hover:bg-slate-50 transition-colors border border-slate-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectRequest}
                  disabled={!rejectReason.trim() || rejectingRequest}
                  className="px-4 py-2 bg-red-600 text-white font-medium text-sm rounded-xl hover:bg-red-700 transition-colors shadow-sm disabled:opacity-50 flex items-center gap-2"
                >
                  {rejectingRequest ? 'Declining...' : 'Confirm Decline'}
                </button>
              </div>
            </div>
          </div>
        )
      }

      <ScheduleInterviewModal
        isOpen={showScheduleInterviewModal}
        onClose={() => setShowScheduleInterviewModal(false)}
        onSchedule={handleScheduleInterview}
        candidate={interviewCandidate || {}}
        currentUser={currentUser}
      />

    </div >
  );
};

export default StaffDashboard;
